import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import api, { getAccessToken } from '../services/api';
import { Card, Button, Spinner, PageHeader, StatCard, PasswordInput } from '../components/common';
import { formatDataOra, ROL_LABELS } from '../utils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';

const adminAPI = {
  status:           () => api.get('/admin/status').then(r => r.data.data),
  getConfigurare:   () => api.get('/admin/configurare').then(r => r.data.data),
  saveConfigurare:  (d) => api.patch('/admin/configurare', { configurari: d }),
  getUtilizatori:   () => api.get('/admin/utilizatori').then(r => r.data.data),
  createUser:       (d) => api.post('/admin/utilizatori', d),
  updateUser:       (id, d) => api.put('/admin/utilizatori/' + id, d),
  resetParola:      (id, d) => api.patch('/admin/utilizatori/' + id + '/parola', d),
  toggleUser:       (id) => api.patch('/admin/utilizatori/' + id + '/toggle'),
  toggleOTP:        (id, enabled) => api.patch('/admin/utilizatori/' + id + '/otp', { enabled }),
  deleteUser:       (id) => api.delete('/admin/utilizatori/' + id),
  getDepartamente:  () => api.get('/admin/departamente').then(r => r.data.data),
  createDept:       (d) => api.post('/admin/departamente', d),
  updateDept:       (id, d) => api.put('/admin/departamente/' + id, d),
  deleteDept:       (id) => api.delete('/admin/departamente/' + id),
  getCategorii:     () => api.get('/admin/categorii').then(r => r.data.data),
  createCategorie:  (d) => api.post('/admin/categorii', d),
  updateCategorie:  (id, d) => api.put('/admin/categorii/' + id, d),
  deleteCategorie:  (id) => api.delete('/admin/categorii/' + id),
  getAudit:         (params) => api.get('/admin/audit', { params }).then(r => r.data),
  exportAudit:      (params) => api.get('/admin/audit/export', { params, responseType: 'blob' }),
  getLicenseStatus:  () => api.get('/admin/license/status').then(r => r.data.data),
  saveLicenseToken:  (token) => api.put('/admin/license/token', { token }).then(r => r.data.data),
  recheckLicense:    () => api.post('/admin/license/recheck').then(r => r.data.data),
  getSmtpConfig:     () => api.get('/admin/settings/smtp').then(r => r.data.data),
  saveSmtpConfig:    (d) => api.put('/admin/settings/smtp', d),
  testSmtp:          () => api.post('/admin/settings/smtp/test').then(r => r.data),
  broadcast:        (d) => api.post('/admin/comunicare/broadcast', d),
  getWorkflows:     ()  => api.get('/admin/workflows').then(r => r.data.data),
  getWorkflow:      (id) => api.get(`/admin/workflows/${id}`).then(r => r.data.data),
  createWorkflow:   (d) => api.post('/admin/workflows', d),
  updateWorkflow:   (id, d) => api.put(`/admin/workflows/${id}`, d),
  toggleWorkflow:   (id) => api.patch(`/admin/workflows/${id}/toggle`),
  setImplicit:      (id) => api.patch(`/admin/workflows/${id}/set-implicit`),
  deleteWorkflow:   (id) => api.delete(`/admin/workflows/${id}`),
  getGhisee:           () => api.get('/programari/admin/ghisee').then(r => r.data.data),
  createGhiseu:        (d) => api.post('/programari/admin/ghisee', d),
  updateGhiseu:        (id, d) => api.put('/programari/admin/ghisee/' + id, d),
  toggleGhiseu:        (id) => api.patch('/programari/admin/ghisee/' + id + '/toggle'),
  getGhiseuProgram:    (id) => api.get('/programari/admin/ghisee/' + id + '/program').then(r => r.data.data),
  updateGhiseuProgram: (id, program) => api.put('/programari/admin/ghisee/' + id + '/program', { program }),
  // Doc requirements
  getDocReqs:         ()  => api.get('/doc-req/admin/all').then(r => r.data.data),
  createDocReq:       (d) => api.post('/doc-req', d),
  deleteDocReq:       (id) => api.delete(`/doc-req/${id}`),
  patchDocReq:        (id, d) => api.patch(`/doc-req/${id}`, d),
  // Tipizate
  getTipizate:        ()  => api.get('/tipizate/admin/all').then(r => r.data.data),
  uploadTipizat:      (fd) => api.post('/tipizate', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  toggleTipizat:      (id) => api.patch(`/tipizate/${id}/toggle`),
  deleteTipizat:      (id) => api.delete(`/tipizate/${id}`),
  // Impozite
  cautaUtilizatori:   (q) => api.get(`/impozite/cautare-utilizatori?q=${encodeURIComponent(q)}`).then(r => r.data.data),
  getProprietati:     (pid) => api.get(`/impozite/proprietati?proprietar_id=${pid}`).then(r => r.data.data),
  createProprietate:  (d) => api.post('/impozite/proprietati', d),
  updateProprietate:  (id, d) => api.put(`/impozite/proprietati/${id}`, d),
  deleteProprietate:  (id) => api.delete(`/impozite/proprietati/${id}`),
  getImpozite:        (pid, an) => api.get(`/impozite?proprietar_id=${pid}&an=${an}`).then(r => r.data.data),
  calculeazaImpozit:  (d) => api.post('/impozite/calculeaza', d),
  marcheazaPlatit:    (id, d) => api.patch(`/impozite/${id}/platit`, d),
  // Backup
  getBackupConfig:    () => api.get('/backup/config').then(r => r.data.data),
  saveBackupConfig:   (d) => api.put('/backup/config', d).then(r => r.data.data),
  testBackupConn:     (d) => api.post('/backup/test-connection', d).then(r => r.data),
  runBackup:          () => api.post('/backup/run').then(r => r.data),
  getBackupList:      () => api.get('/backup/list').then(r => r.data.data),
  downloadBackup:     (tip, fisier) => api.get(`/backup/download/${tip}/${fisier}`, { responseType: 'blob' }),
};

const ROLURI = [
  { value: 'functionar', label: 'Funcționar' },
  { value: 'sef_departament', label: 'Șef departament' },
  { value: 'secretar_general', label: 'Secretar General' },
  { value: 'admin', label: 'Administrator' },
  { value: 'primar', label: 'Primar' },
];

export default function AdminPage() {
  const { user } = useAuth();
  const isSuperAdmin = user && user.rol === 'superadmin';
  const [tab, setTab] = useState('status');

  const taburi = [
    ['status', 'Status'],
    ['utilizatori', 'Utilizatori'],
    ['departamente', 'Departamente'],
    ['ghisee', 'Ghișee'],
    ['categorii', 'Categorii'],
    ['configurare', 'Configurare'],
    ['audit', 'Audit log'],
    ['comunicare', 'Comunicare'],
    ['fluxuri', 'Fluxuri documente'],
    ['impozite', 'Impozite & Taxe'],
    ['tipizate', 'Formulare tipizate'],
    ['docreqs', 'Doc. obligatorii'],
    ['backup', 'Backup'],
  ];
  if (isSuperAdmin) taburi.push(['licenta', 'Licențe']);
  if (isSuperAdmin) taburi.push(['email', 'Email & SMTP']);

  return (
    <Layout>
      <PageHeader title="Administrare" subtitle="Management complet primărie" />
      <div style={s.tabs}>
        {taburi.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={Object.assign({}, s.tabBtn, tab === key ? s.tabActive : {})}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'status'       && <TabStatus />}
      {tab === 'utilizatori'  && <TabUtilizatori />}
      {tab === 'departamente' && <TabDepartamente />}
      {tab === 'ghisee'       && <TabGhisee />}
      {tab === 'categorii'    && <TabCategorii />}
      {tab === 'configurare'  && <TabConfigurare />}
      {tab === 'audit'        && <TabAudit />}
      {tab === 'comunicare'   && <TabComunicare />}
      {tab === 'fluxuri'      && <TabFluxuri />}
      {tab === 'impozite'     && <TabImpozite />}
      {tab === 'tipizate'     && <TabTipizate />}
      {tab === 'docreqs'      && <TabDocReqs />}
      {tab === 'backup'       && <TabBackup />}
      {tab === 'licenta'      && isSuperAdmin && <TabLicenta />}
      {tab === 'email'        && isSuperAdmin && <TabEmailSMTP />}
    </Layout>
  );
}

function TabStatus() {
  const { data, isLoading } = useQuery('admin-status', adminAPI.status);
  if (isLoading) return React.createElement(Spinner, null);
  return React.createElement('div', null,
    React.createElement('div', { style: s.grid3 },
      React.createElement(StatCard, { label: 'Utilizatori activi', value: (data && data.stats && data.stats.usersActivi) || 0, color: '#2d2d7e' }),
      React.createElement(StatCard, { label: 'Total cereri', value: (data && data.stats && data.stats.totalCereri) || 0, color: '#1d9e75' }),
      React.createElement(StatCard, { label: 'Stocare', value: ((data && data.stats && data.stats.stocareMB) || 0) + ' MB', color: '#854f0b' })
    ),
    React.createElement(Card, { title: 'Informații sistem' },
      [['Primărie', data && data.primaria], ['Versiune', data && data.versiune],
       ['Licență', data && data.license && data.license.valid ? '✓ Activă' : '✗ Invalidă'],
       ['Zile rămase', data && data.license && data.license.daysLeft != null ? data.license.daysLeft + ' zile' : '—']]
      .map(([k, v]) => React.createElement('div', { key: k, style: s.infoRow },
        React.createElement('span', { style: s.infoKey }, k + ':'),
        React.createElement('span', { style: s.infoVal }, v || '—')
      ))
    )
  );
}

function TabUtilizatori() {
  const { user } = useAuth();
  const isSuperAdmin = user && user.rol === 'superadmin';
  const roluriForm = [
    { value: 'functionar', label: 'Funcționar' },
    { value: 'sef_departament', label: 'Șef departament' },
    { value: 'admin', label: 'Administrator' },
    { value: 'primar', label: 'Primar' },
    ...(isSuperAdmin ? [{ value: 'superadmin', label: 'Super Administrator' }] : []),
  ];
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const { register: r1, handleSubmit: h1, reset: reset1, formState: { errors: e1 } } = useForm();
  const { register: r2, handleSubmit: h2 } = useForm();
  const { register: r3, handleSubmit: h3, reset: reset3 } = useForm();
  const [search, setSearch] = useState('');
  const [filtruRol, setFiltruRol] = useState('');
  const { data: utilizatori, isLoading } = useQuery('admin-utilizatori', adminAPI.getUtilizatori);

  const utilizatoriFiltrati = (utilizatori || []).filter(function(u) {
    const matchSearch = !search || 
      (u.prenume + ' ' + u.nume).toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.departament || '').toLowerCase().includes(search.toLowerCase());
    const matchRol = !filtruRol || u.rol === filtruRol;
    return matchSearch && matchRol;
  });
  const { data: departamente } = useQuery('admin-departamente', adminAPI.getDepartamente);
  const refresh = () => qc.invalidateQueries('admin-utilizatori');
  const createMut = useMutation(adminAPI.createUser, {
    onSuccess: () => { toast.success('Utilizator creat'); refresh(); reset1(); setShowForm(false); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'),
  });
  const updateMut = useMutation(function(p) { return adminAPI.updateUser(p.id, p.data); }, {
    onSuccess: () => { toast.success('Actualizat'); refresh(); setEditUser(null); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'),
  });
  const passMut = useMutation(function(p) { return adminAPI.resetParola(p.id, p.data); }, {
    onSuccess: () => { toast.success('Parolă resetată'); setResetUser(null); reset3(); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'),
  });
  const toggleMut    = useMutation(adminAPI.toggleUser, { onSuccess: () => { toast.success('Status actualizat'); refresh(); } });
  const otpToggleMut = useMutation(function(p) { return adminAPI.toggleOTP(p.id, p.enabled); }, {
    onSuccess: () => { toast.success('2FA actualizat'); refresh(); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare 2FA'),
  });
  const deleteUserMut = useMutation(adminAPI.deleteUser, {
    onSuccess: () => { toast.success('Utilizator șters'); refresh(); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare la ștergere'),
  });
  if (isLoading) return React.createElement(Spinner, null);
  const deptList = departamente || [];

  return React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' } },
      React.createElement(Button, { onClick: () => { setShowForm(!showForm); setEditUser(null); setResetUser(null); } },
        showForm ? 'Anulează' : '+ Utilizator nou')
    ),
    showForm && React.createElement(Card, { title: 'Utilizator nou' },
      React.createElement('form', { onSubmit: h1(function(d) { createMut.mutate(d); }), style: s.formGrid, autoComplete: 'off' },
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Prenume *'), React.createElement('input', Object.assign({ style: s.inp, autoComplete: 'off' }, r1('prenume', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Nume *'), React.createElement('input', Object.assign({ style: s.inp, autoComplete: 'off' }, r1('nume', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Email *'), React.createElement('input', Object.assign({ type: 'email', style: s.inp, autoComplete: 'off' }, r1('email', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Telefon'), React.createElement('input', Object.assign({ type: 'tel', style: s.inp, autoComplete: 'off' }, r1('phone')))),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Parolă * (min 8, 1 majusculă, 1 cifră)'),
          React.createElement(PasswordInput, Object.assign({ style: s.inp, autoComplete: 'new-password' }, r1('password', { required: true, minLength: 8, validate: function(v) { return /(?=.*[A-Z])(?=.*\d)/.test(v) || 'Necesită majusculă și cifră'; } }))),
          e1.password && React.createElement('span', { style: s.errMsg }, e1.password.message || 'Parolă invalidă')
        ),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Rol *'),
          React.createElement('select', Object.assign({ style: s.inp }, r1('rol', { required: true })),
            roluriForm.map(function(ro) { return React.createElement('option', { key: ro.value, value: ro.value }, ro.label); })
          )
        ),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Departament'),
          React.createElement('select', Object.assign({ style: s.inp }, r1('departament')),
            React.createElement('option', { value: '' }, 'Fără departament'),
            deptList.map(function(d) { return React.createElement('option', { key: d.id, value: d.cod }, d.nume); })
          )
        ),
        React.createElement('div', { style: { gridColumn: '1/-1', display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: createMut.isLoading }, createMut.isLoading ? 'Se creează...' : 'Creează utilizatorul'),
          React.createElement(Button, { onClick: function() { setShowForm(false); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    editUser && React.createElement(Card, { key: editUser.id, title: 'Editare: ' + editUser.prenume + ' ' + editUser.nume },
      React.createElement('form', { onSubmit: h2(function(d) { updateMut.mutate({ id: editUser.id, data: d }); }), style: s.formGrid, autoComplete: 'off' },
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Prenume'), React.createElement('input', Object.assign({ style: s.inp, defaultValue: editUser.prenume, autoComplete: 'off' }, r2('prenume')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Nume'), React.createElement('input', Object.assign({ style: s.inp, defaultValue: editUser.nume, autoComplete: 'off' }, r2('nume')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Email'), React.createElement('input', Object.assign({ type: 'email', style: s.inp, defaultValue: editUser.email, autoComplete: 'off' }, r2('email')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Telefon'), React.createElement('input', Object.assign({ style: s.inp, defaultValue: editUser.phone || '', autoComplete: 'off' }, r2('phone')))),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Rol'),
          React.createElement('select', Object.assign({ style: s.inp, defaultValue: editUser.rol }, r2('rol')),
            roluriForm.map(function(ro) { return React.createElement('option', { key: ro.value, value: ro.value }, ro.label); })
          )
        ),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Departament'),
          React.createElement('select', Object.assign({ style: s.inp, defaultValue: editUser.departament || '' }, r2('departament')),
            React.createElement('option', { value: '' }, 'Fără departament'),
            deptList.map(function(d) { return React.createElement('option', { key: d.id, value: d.cod }, d.nume); })
          )
        ),
        React.createElement('div', { style: { gridColumn: '1/-1', display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: updateMut.isLoading }, 'Salvează modificările'),
          React.createElement(Button, { onClick: function() { setEditUser(null); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    resetUser && React.createElement(Card, { title: 'Reset parolă: ' + resetUser.prenume + ' ' + resetUser.nume },
      React.createElement('form', { onSubmit: h3(function(d) { passMut.mutate({ id: resetUser.id, data: d }); }), style: { display: 'flex', gap: '12px', maxWidth: '400px' } },
        React.createElement('div', { style: Object.assign({}, s.fld, { flex: 1 }) },
          React.createElement('label', { style: s.lbl }, 'Parolă nouă *'),
          React.createElement(PasswordInput, Object.assign({ style: s.inp, autoComplete: 'new-password' }, r3('password', { required: true, minLength: 8 })))
        ),
        React.createElement('div', { style: { display: 'flex', gap: '8px', alignItems: 'flex-end' } },
          React.createElement(Button, { type: 'submit', disabled: passMut.isLoading }, 'Resetează'),
          React.createElement(Button, { onClick: function() { setResetUser(null); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    React.createElement('div', { style: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' } },
      React.createElement('input', {
        type: 'text', placeholder: 'Cauta dupa nume, email, departament...',
        value: search, onChange: function(e) { setSearch(e.target.value); },
        style: Object.assign({}, s.inp, { flex: 2, minWidth: '200px' })
      }),
      React.createElement('select', {
        value: filtruRol, onChange: function(e) { setFiltruRol(e.target.value); },
        style: Object.assign({}, s.inp, { minWidth: '160px' })
      },
        React.createElement('option', { value: '' }, 'Toate rolurile'),
        [['cetatean','Cetățean'],['functionar','Funcționar'],['sef_departament','Șef departament'],['admin','Administrator'],['primar','Primar'],['superadmin','Super Administrator']].map(function(r) {
          return React.createElement('option', { key: r[0], value: r[0] }, r[1]);
        })
      ),
      search || filtruRol ? React.createElement('button', {
        onClick: function() { setSearch(''); setFiltruRol(''); },
        style: Object.assign({}, s.btnMic, { padding: '8px 14px' })
      }, 'X Reseteaza') : null
    ),
    React.createElement(Card, { title: 'Utilizatori (' + utilizatoriFiltrati.length + ' din ' + ((utilizatori && utilizatori.length) || 0) + ')' },
      React.createElement('div', { style: s.thRow },
        ['Nume','Email','Rol','Departament','Activ','Ultimul login','Acțiuni'].map(function(h) {
          return React.createElement('span', { key: h, style: s.th }, h);
        })
      ),
      utilizatoriFiltrati.map(function(u) {
        return React.createElement('div', { key: u.id, style: s.tdRow },
          React.createElement('span', { style: s.td }, u.prenume + ' ' + u.nume),
          React.createElement('span', { style: Object.assign({}, s.td, { fontSize: '12px', color: u.email?.endsWith('@portofelet.local') ? '#9ca3af' : 'inherit' }) }, u.email?.endsWith('@portofelet.local') ? '🪪 EUDI Wallet' : u.email),
          React.createElement('span', { style: s.td }, ROL_LABELS[u.rol] || u.rol),
          React.createElement('span', { style: s.td }, u.departament || '—'),
          React.createElement('span', { style: Object.assign({}, s.td, { color: u.activ ? '#059669' : '#dc2626', fontWeight: '700' }) }, u.activ ? 'Da' : 'Nu'),
          React.createElement('span', { style: Object.assign({}, s.td, { fontSize: '11px' }) }, u.last_login ? formatDataOra(u.last_login) : 'Niciodată'),
          React.createElement('div', { style: { display: 'flex', gap: '4px', flexWrap: 'wrap' } },
            React.createElement('button', { onClick: function() { setEditUser(u); setShowForm(false); setResetUser(null); }, style: s.btnMic }, '✏️ Edit'),
            React.createElement('button', { onClick: function() { setResetUser(u); setEditUser(null); setShowForm(false); }, style: s.btnMic }, '🔑 Parolă'),
            React.createElement('button', { onClick: function() { toggleMut.mutate(u.id); }, style: Object.assign({}, s.btnMic, { color: u.activ ? '#dc2626' : '#059669' }) }, u.activ ? '⊘ Dezact.' : '✓ Act.'),
            u.rol !== 'cetatean' && u.rol !== 'superadmin' && React.createElement('button', {
              onClick: function() { otpToggleMut.mutate({ id: u.id, enabled: !u.otp_enabled }); },
              style: Object.assign({}, s.btnMic, { color: u.otp_enabled ? '#059669' : '#dc2626' }),
              title: u.otp_enabled ? '2FA activ — click pentru dezactivare' : '2FA inactiv — click pentru activare'
            }, u.otp_enabled ? '🔐 2FA On' : '🔓 2FA Off'),
            u.rol !== 'superadmin' && u.id !== user.id && React.createElement('button', {
              onClick: function() { if (window.confirm('Ștergi utilizatorul ' + u.prenume + ' ' + u.nume + '? Acțiunea este ireversibilă.')) { deleteUserMut.mutate(u.id); } },
              style: Object.assign({}, s.btnMic, { color: '#dc2626', borderColor: '#fca5a5' }),
            }, '🗑 Șterge')
          )
        );
      })
    )
  );
}

function TabDepartamente() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const { register: r1, handleSubmit: h1, reset: reset1 } = useForm();
  const { register: r2, handleSubmit: h2 } = useForm();
  const { data, isLoading } = useQuery('admin-departamente', adminAPI.getDepartamente);
  const { data: utilizatori } = useQuery('admin-utilizatori', adminAPI.getUtilizatori);
  const refresh = () => qc.invalidateQueries('admin-departamente');
  const createMut = useMutation(adminAPI.createDept, {
    onSuccess: () => { toast.success('Departament creat'); refresh(); reset1(); setShowForm(false); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'),
  });
  const updateMut = useMutation(function(p) { return adminAPI.updateDept(p.id, p.data); }, {
    onSuccess: () => { toast.success('Actualizat'); refresh(); setEditItem(null); },
  });
  const deleteMut = useMutation(adminAPI.deleteDept, {
    onSuccess: () => { toast.success('Dezactivat'); refresh(); },
  });
  if (isLoading) return React.createElement(Spinner, null);
  const functionari = (utilizatori || []).filter(function(u) { return ['functionar','sef_departament','admin','primar'].includes(u.rol); });

  return React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' } },
      React.createElement(Button, { onClick: function() { setShowForm(!showForm); setEditItem(null); } }, showForm ? 'Anulează' : '+ Departament nou')
    ),
    showForm && React.createElement(Card, { title: 'Departament nou' },
      React.createElement('form', { onSubmit: h1(function(d) { createMut.mutate(d); }), style: s.formGrid },
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Cod (ex: URBANISM) *'), React.createElement('input', Object.assign({ style: s.inp, placeholder: 'COD_DEPT' }, r1('cod', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Denumire completă *'), React.createElement('input', Object.assign({ style: s.inp }, r1('nume', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Email departament'), React.createElement('input', Object.assign({ type: 'email', style: s.inp, autoComplete: 'off' }, r1('email')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Termen legal (zile)'), React.createElement('input', Object.assign({ type: 'number', style: s.inp, defaultValue: '30' }, r1('termenZile')))),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Șef departament'),
          React.createElement('select', Object.assign({ style: s.inp }, r1('sefId')),
            React.createElement('option', { value: '' }, 'Fără șef'),
            functionari.map(function(u) { return React.createElement('option', { key: u.id, value: u.id }, u.prenume + ' ' + u.nume); })
          )
        ),
        React.createElement('div', { style: { gridColumn: '1/-1', display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: createMut.isLoading }, createMut.isLoading ? 'Se creează...' : 'Creează'),
          React.createElement(Button, { onClick: function() { setShowForm(false); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    editItem && React.createElement(Card, { title: 'Editare: ' + editItem.nume },
      React.createElement('form', { onSubmit: h2(function(d) { updateMut.mutate({ id: editItem.id, data: d }); }), style: s.formGrid },
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Denumire'), React.createElement('input', Object.assign({ style: s.inp, defaultValue: editItem.nume }, r2('nume')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Email'), React.createElement('input', Object.assign({ type: 'email', style: s.inp, defaultValue: editItem.email || '', autoComplete: 'off' }, r2('email')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Termen (zile)'), React.createElement('input', Object.assign({ type: 'number', style: s.inp, defaultValue: editItem.termen_zile }, r2('termenZile')))),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Șef departament'),
          React.createElement('select', Object.assign({ style: s.inp, defaultValue: editItem.sef_id || '' }, r2('sefId')),
            React.createElement('option', { value: '' }, 'Fără șef'),
            functionari.map(function(u) { return React.createElement('option', { key: u.id, value: u.id }, u.prenume + ' ' + u.nume); })
          )
        ),
        React.createElement('div', { style: { gridColumn: '1/-1', display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: updateMut.isLoading }, 'Salvează'),
          React.createElement(Button, { onClick: function() { setEditItem(null); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    React.createElement(Card, { title: 'Departamente (' + ((data && data.length) || 0) + ')' },
      (data || []).map(function(d) {
        return React.createElement('div', { key: d.id, style: { display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'center' } },
          React.createElement('span', { style: { fontFamily: 'monospace', fontSize: '12px', color: '#888', minWidth: '120px' } }, d.cod),
          React.createElement('span', { style: { flex: 2, fontWeight: '500', fontSize: '14px' } }, d.nume),
          React.createElement('span', { style: { fontSize: '12px', color: '#6b7280', flex: 1 } }, d.sef_complet || '—'),
          React.createElement('span', { style: { fontSize: '12px', color: '#888' } }, d.termen_zile + ' zile'),
          React.createElement('span', { style: { fontSize: '12px', fontWeight: '700', color: d.activ ? '#059669' : '#dc2626' } }, d.activ ? 'Activ' : 'Inactiv'),
          React.createElement('div', { style: { display: 'flex', gap: '4px' } },
            React.createElement('button', { onClick: function() { setEditItem(d); setShowForm(false); }, style: s.btnMic }, '✏️ Edit'),
            d.activ && React.createElement('button', { onClick: function() { deleteMut.mutate(d.id); }, style: Object.assign({}, s.btnMic, { color: '#dc2626' }) }, '⊘ Dezact.')
          )
        );
      })
    )
  );
}

function TabCategorii() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const { register: r1, handleSubmit: h1, reset: reset1 } = useForm();
  const { register: r2, handleSubmit: h2 } = useForm();
  const { data, isLoading } = useQuery('admin-categorii', adminAPI.getCategorii);
  const { data: departamente } = useQuery('admin-departamente', adminAPI.getDepartamente);
  const refresh = () => qc.invalidateQueries('admin-categorii');
  const createMut = useMutation(adminAPI.createCategorie, {
    onSuccess: () => { toast.success('Categorie creată'); refresh(); reset1(); setShowForm(false); },
    onError: (e) => toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'),
  });
  const updateMut = useMutation(function(p) { return adminAPI.updateCategorie(p.id, p.data); }, {
    onSuccess: () => { toast.success('Actualizat'); refresh(); setEditItem(null); },
  });
  const deleteMut = useMutation(adminAPI.deleteCategorie, { onSuccess: () => { toast.success('Dezactivat'); refresh(); } });
  if (isLoading) return React.createElement(Spinner, null);
  const depts = (departamente || []).filter(function(d) { return d.activ; });

  return React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' } },
      React.createElement(Button, { onClick: function() { setShowForm(!showForm); setEditItem(null); } }, showForm ? 'Anulează' : '+ Categorie nouă')
    ),
    showForm && React.createElement(Card, { title: 'Categorie nouă' },
      React.createElement('form', { onSubmit: h1(function(d) { createMut.mutate(d); }), style: s.formGrid },
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Cod *'), React.createElement('input', Object.assign({ style: s.inp }, r1('cod', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Denumire *'), React.createElement('input', Object.assign({ style: s.inp }, r1('nume', { required: true })))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Descriere'), React.createElement('input', Object.assign({ style: s.inp }, r1('descriere')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Termen (zile)'), React.createElement('input', Object.assign({ type: 'number', style: s.inp, defaultValue: '30' }, r1('termenZile')))),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Departament'),
          React.createElement('select', Object.assign({ style: s.inp }, r1('departamentId')),
            React.createElement('option', { value: '' }, 'Fără departament'),
            depts.map(function(d) { return React.createElement('option', { key: d.id, value: d.id }, d.nume); })
          )
        ),
        React.createElement('div', { style: { gridColumn: '1/-1', display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: createMut.isLoading }, 'Creează'),
          React.createElement(Button, { onClick: function() { setShowForm(false); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    editItem && React.createElement(Card, { title: 'Editare: ' + editItem.nume },
      React.createElement('form', { onSubmit: h2(function(d) { updateMut.mutate({ id: editItem.id, data: d }); }), style: s.formGrid },
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Denumire'), React.createElement('input', Object.assign({ style: s.inp, defaultValue: editItem.nume }, r2('nume')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Descriere'), React.createElement('input', Object.assign({ style: s.inp, defaultValue: editItem.descriere || '' }, r2('descriere')))),
        React.createElement('div', { style: s.fld }, React.createElement('label', { style: s.lbl }, 'Termen (zile)'), React.createElement('input', Object.assign({ type: 'number', style: s.inp, defaultValue: editItem.termen_zile }, r2('termenZile')))),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Departament'),
          React.createElement('select', Object.assign({ style: s.inp, defaultValue: editItem.departament_id || '' }, r2('departamentId')),
            React.createElement('option', { value: '' }, 'Fără departament'),
            depts.map(function(d) { return React.createElement('option', { key: d.id, value: d.id }, d.nume); })
          )
        ),
        React.createElement('div', { style: { gridColumn: '1/-1', display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: updateMut.isLoading }, 'Salvează'),
          React.createElement(Button, { onClick: function() { setEditItem(null); }, variant: 'ghost' }, 'Anulează')
        )
      )
    ),
    React.createElement(Card, { title: 'Categorii (' + ((data && data.length) || 0) + ')' },
      (data || []).map(function(c) {
        return React.createElement('div', { key: c.id, style: { display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'center' } },
          React.createElement('span', { style: { fontFamily: 'monospace', fontSize: '11px', color: '#888', minWidth: '120px' } }, c.cod),
          React.createElement('span', { style: { flex: 2, fontWeight: '500', fontSize: '13px' } }, c.nume),
          React.createElement('span', { style: { fontSize: '12px', color: '#6b7280', flex: 1 } }, c.departament_nume || '—'),
          React.createElement('span', { style: { fontSize: '12px', color: '#888' } }, c.termen_zile + ' zile'),
          React.createElement('span', { style: { fontSize: '12px', fontWeight: '700', color: c.activ ? '#059669' : '#dc2626' } }, c.activ ? 'Activă' : 'Inactivă'),
          React.createElement('div', { style: { display: 'flex', gap: '4px' } },
            React.createElement('button', { onClick: function() { setEditItem(c); setShowForm(false); }, style: s.btnMic }, '✏️'),
            c.activ && React.createElement('button', { onClick: function() { deleteMut.mutate(c.id); }, style: Object.assign({}, s.btnMic, { color: '#dc2626' }) }, '⊘')
          )
        );
      })
    )
  );
}

const ZI_SAPT = ['', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];
const DURATE  = [10, 15, 20, 30, 45, 60];

function programDefault() {
  return [1,2,3,4,5].map(function(zi) {
    return { ziSaptamana: zi, oraStart: '08:00', oraSfarsit: '16:00', durataSlot: 30, activ: true };
  }).concat([6,7].map(function(zi) {
    return { ziSaptamana: zi, oraStart: '09:00', oraSfarsit: '13:00', durataSlot: 30, activ: false };
  }));
}

function TabGhisee() {
  var { data: departamente } = useQuery('dept-list', adminAPI.getDepartamente);
  var qc = useQueryClient();

  var [showForm, setShowForm] = useState(false);
  var [editing, setEditing]   = useState(null);
  var [form, setForm]         = useState({ denumire: '', locatie: '', departamentId: '' });
  var [programFor, setProgramFor] = useState(null); // ghiseu id al carui program e editat
  var [program, setProgram]   = useState(programDefault());

  var { data: ghisee, isLoading } = useQuery('admin-ghisee', adminAPI.getGhisee);

  var createMut = useMutation(adminAPI.createGhiseu, {
    onSuccess: function() { qc.invalidateQueries('admin-ghisee'); setShowForm(false); toast.success('Ghișeu creat!'); },
    onError:   function(e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'); },
  });
  var updateMut = useMutation(function(d) { return adminAPI.updateGhiseu(editing.id, d); }, {
    onSuccess: function() { qc.invalidateQueries('admin-ghisee'); setShowForm(false); setEditing(null); toast.success('Ghișeu actualizat!'); },
    onError:   function(e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare'); },
  });
  var toggleMut = useMutation(adminAPI.toggleGhiseu, {
    onSuccess: function() { qc.invalidateQueries('admin-ghisee'); },
    onError:   function(e) { toast.error('Eroare toggle'); },
  });
  var programMut = useMutation(function() { return adminAPI.updateGhiseuProgram(programFor, program); }, {
    onSuccess: function() { qc.invalidateQueries('admin-ghisee'); setProgramFor(null); toast.success('Program salvat!'); },
    onError:   function(e) { toast.error('Eroare la salvarea programului'); },
  });

  function deschideCreate() {
    setEditing(null);
    setForm({ denumire: '', locatie: '', departamentId: '' });
    setShowForm(true);
  }

  function deschideEdit(g) {
    setEditing(g);
    setForm({ denumire: g.denumire, locatie: g.locatie || '', departamentId: g.departament_id || '' });
    setShowForm(true);
  }

  function deschideProgram(g) {
    var existing = Array.isArray(g.program) && g.program.length > 0
      ? g.program.map(function(p) { return { ziSaptamana: p.zi_saptamana, oraStart: p.ora_start.substring(0,5), oraSfarsit: p.ora_sfarsit.substring(0,5), durataSlot: p.durata_slot, activ: p.activ }; })
      : programDefault();
    // Completăm zilele lipsă
    var complete = [1,2,3,4,5,6,7].map(function(zi) {
      return existing.find(function(p) { return p.ziSaptamana === zi; }) ||
             { ziSaptamana: zi, oraStart: '08:00', oraSfarsit: '16:00', durataSlot: 30, activ: false };
    });
    setProgram(complete);
    setProgramFor(g.id);
  }

  function submitForm(e) {
    e.preventDefault();
    var d = { denumire: form.denumire, locatie: form.locatie, departamentId: form.departamentId || undefined };
    if (editing) updateMut.mutate(d);
    else createMut.mutate(d);
  }

  function updZi(idx, field, val) {
    setProgram(function(p) {
      var nou = p.slice();
      nou[idx] = Object.assign({}, nou[idx], { [field]: val });
      return nou;
    });
  }

  var inp = { style: { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%', boxSizing: 'border-box' } };
  var lbl = { style: { fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px', fontWeight: 500 } };

  // Editor program
  if (programFor) {
    var ghiseuSel = (ghisee || []).find(function(g) { return g.id === programFor; });
    return React.createElement(Card, { title: 'Program ghișeu: ' + (ghiseuSel ? ghiseuSel.denumire : '') },
      React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } },
        React.createElement('thead', null,
          React.createElement('tr', { style: { borderBottom: '2px solid #e5e7eb' } },
            ['Zi', 'Deschis', 'Ora start', 'Ora sfârșit', 'Durată slot (min)'].map(function(h) {
              return React.createElement('th', { key: h, style: { padding: '8px 12px', color: '#6b7280', fontWeight: 600, textAlign: 'left' } }, h);
            })
          )
        ),
        React.createElement('tbody', null,
          program.map(function(zi, i) {
            return React.createElement('tr', { key: zi.ziSaptamana, style: { borderBottom: '1px solid #f3f4f6' } },
              React.createElement('td', { style: { padding: '10px 12px', fontWeight: 600, color: '#374151', width: '100px' } }, ZI_SAPT[zi.ziSaptamana]),
              React.createElement('td', { style: { padding: '10px 12px' } },
                React.createElement('input', { type: 'checkbox', checked: zi.activ, onChange: function(e) { updZi(i, 'activ', e.target.checked); }, style: { width: '18px', height: '18px', cursor: 'pointer' } })
              ),
              React.createElement('td', { style: { padding: '10px 12px' } },
                React.createElement('input', { type: 'time', value: zi.oraStart, disabled: !zi.activ, onChange: function(e) { updZi(i, 'oraStart', e.target.value); }, style: Object.assign({}, inp.style, { width: '120px', opacity: zi.activ ? 1 : 0.4 }) })
              ),
              React.createElement('td', { style: { padding: '10px 12px' } },
                React.createElement('input', { type: 'time', value: zi.oraSfarsit, disabled: !zi.activ, onChange: function(e) { updZi(i, 'oraSfarsit', e.target.value); }, style: Object.assign({}, inp.style, { width: '120px', opacity: zi.activ ? 1 : 0.4 }) })
              ),
              React.createElement('td', { style: { padding: '10px 12px' } },
                React.createElement('select', { value: zi.durataSlot, disabled: !zi.activ, onChange: function(e) { updZi(i, 'durataSlot', parseInt(e.target.value)); }, style: Object.assign({}, inp.style, { width: '130px', opacity: zi.activ ? 1 : 0.4 }) },
                  DURATE.map(function(d) { return React.createElement('option', { key: d, value: d }, d + ' minute'); })
                )
              )
            );
          })
        )
      ),
      React.createElement('div', { style: { display: 'flex', gap: '10px', marginTop: '20px' } },
        React.createElement(Button, { onClick: function() { programMut.mutate(); }, disabled: programMut.isLoading }, programMut.isLoading ? 'Se salvează...' : 'Salvează programul'),
        React.createElement(Button, { variant: 'ghost', onClick: function() { setProgramFor(null); } }, '← Înapoi la ghișee')
      )
    );
  }

  // Lista ghișee
  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' } },
      React.createElement('h3', { style: { margin: 0, fontSize: '16px', color: '#1a1a2e' } }, 'Ghișee active și inactive'),
      React.createElement(Button, { onClick: deschideCreate }, '+ Ghișeu nou')
    ),

    showForm && React.createElement('div', { style: { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '20px' } },
      React.createElement('h4', { style: { margin: '0 0 16px', color: '#1a1a2e' } }, editing ? 'Editare ghișeu' : 'Ghișeu nou'),
      React.createElement('form', { onSubmit: submitForm, style: { display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end' } },
        React.createElement('div', { style: { flex: '1 1 200px' } },
          React.createElement('label', lbl, 'Denumire *'),
          React.createElement('input', Object.assign({}, inp, { required: true, placeholder: 'ex: Taxe și Impozite', value: form.denumire, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { denumire: e.target.value }); }); } }))
        ),
        React.createElement('div', { style: { flex: '1 1 180px' } },
          React.createElement('label', lbl, 'Locație'),
          React.createElement('input', Object.assign({}, inp, { placeholder: 'ex: Parter, Camera 1', value: form.locatie, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { locatie: e.target.value }); }); } }))
        ),
        React.createElement('div', { style: { flex: '1 1 200px' } },
          React.createElement('label', lbl, 'Departament'),
          React.createElement('select', Object.assign({}, inp, { value: form.departamentId, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { departamentId: e.target.value }); }); } }),
            React.createElement('option', { value: '' }, '— Fără departament —'),
            (departamente || []).map(function(d) { return React.createElement('option', { key: d.id, value: d.id }, d.nume); })
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: '8px' } },
          React.createElement(Button, { type: 'submit', disabled: createMut.isLoading || updateMut.isLoading }, editing ? 'Salvează' : 'Creează'),
          React.createElement(Button, { type: 'button', variant: 'ghost', onClick: function() { setShowForm(false); setEditing(null); } }, 'Anulează')
        )
      )
    ),

    isLoading && React.createElement(Spinner, null),

    !isLoading && React.createElement('div', { style: { overflowX: 'auto' } },
      React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } },
        React.createElement('thead', null,
          React.createElement('tr', { style: { borderBottom: '2px solid #e5e7eb' } },
            ['Denumire', 'Locație', 'Departament', 'Status', 'Program', 'Acțiuni'].map(function(h) {
              return React.createElement('th', { key: h, style: { padding: '10px 12px', color: '#6b7280', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' } }, h);
            })
          )
        ),
        React.createElement('tbody', null,
          (ghisee || []).map(function(g) {
            var zileActive = Array.isArray(g.program) ? g.program.filter(function(p) { return p.activ; }).map(function(p) { return ZI_SAPT[p.zi_saptamana]; }).join(', ') : '—';
            return React.createElement('tr', { key: g.id, style: { borderBottom: '1px solid #f3f4f6', opacity: g.activ ? 1 : 0.55 } },
              React.createElement('td', { style: { padding: '10px 12px', fontWeight: 600, color: '#1a1a2e' } }, g.denumire),
              React.createElement('td', { style: { padding: '10px 12px', color: '#6b7280' } }, g.locatie || '—'),
              React.createElement('td', { style: { padding: '10px 12px', color: '#6b7280' } }, g.departament_nume || '—'),
              React.createElement('td', { style: { padding: '10px 12px' } },
                React.createElement('span', { style: { fontSize: '12px', padding: '3px 8px', borderRadius: '12px', backgroundColor: g.activ ? '#dcfce7' : '#fee2e2', color: g.activ ? '#166534' : '#991b1b', fontWeight: 600 } },
                  g.activ ? 'Activ' : 'Inactiv'
                )
              ),
              React.createElement('td', { style: { padding: '10px 12px', color: '#6b7280', fontSize: '12px' } }, zileActive || 'Fără program'),
              React.createElement('td', { style: { padding: '10px 12px' } },
                React.createElement('div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap' } },
                  React.createElement(Button, { variant: 'secondary', onClick: function() { deschideEdit(g); } }, 'Editare'),
                  React.createElement(Button, { variant: 'secondary', onClick: function() { deschideProgram(g); } }, 'Program'),
                  React.createElement(Button, {
                    variant: g.activ ? 'danger' : 'ghost',
                    disabled: toggleMut.isLoading,
                    onClick: function() { toggleMut.mutate(g.id); }
                  }, g.activ ? 'Dezactivează' : 'Activează')
                )
              )
            );
          })
        )
      )
    )
  );
}

function TabConfigurare() {
  const [modificate, setModificate] = useState({});
  const { data, isLoading, refetch } = useQuery('admin-configurare', adminAPI.getConfigurare);
  const saveMut = useMutation(
    function() { return adminAPI.saveConfigurare(Object.entries(modificate).map(function(e) { return { cheie: e[0], valoare: e[1] }; })); },
    { onSuccess: function() { toast.success('Configurare salvată'); setModificate({}); refetch(); }, onError: function() { toast.error('Eroare salvare'); } }
  );
  if (isLoading) return React.createElement(Spinner, null);

  const grupe = {
    'Informații primărie': ['PRIMARIA_NUME','PRIMARIA_CUI','PRIMARIA_ADRESA','PRIMARIA_TELEFON','PRIMARIA_EMAIL','PRIMARIA_WEBSITE','PRIMARIA_JUDET','PRIMARIA_LOCALITATE','PRIMAR_NUME'],
    'Email notificări': ['SMTP_HOST','SMTP_PORT','SMTP_USER','SMTP_PASS'],
    'SMS notificări': ['SMS_PROVIDER','SMS_API_KEY'],
    'Setări generale': ['TERMEN_DEFAULT','LOGO_URL'],
    'Email solicitări (IMAP)': ['EMAIL_INGEST_ENABLED','IMAP_HOST','IMAP_PORT','IMAP_USER','IMAP_PASS','EMAIL_INGEST_FOLDER'],
  };

  var cheiBool = ['2FA_ENABLED','EMAIL_INGEST_ENABLED'];
  var config2FA = (data || []).filter(function(d) { return d.cheie === '2FA_ENABLED' || d.cheie === '2FA_ROLURI'; });

  return React.createElement('div', null,
    config2FA.length > 0 && React.createElement(Card, { key: '2fa', title: 'Securitate — Autentificare în 2 pași (2FA)' },
      React.createElement('div', { style: s.formGrid },
        config2FA.map(function(item) {
          var isBool = cheiBool.includes(item.cheie);
          var valCurenta = modificate[item.cheie] !== undefined ? modificate[item.cheie] : item.valoare;
          return React.createElement('div', { key: item.cheie, style: s.fld },
            React.createElement('label', { style: s.lbl }, item.descriere || item.cheie),
            isBool
              ? React.createElement('select', {
                  style: s.inp,
                  value: valCurenta,
                  onChange: function(e) { setModificate(function(m) { var n = Object.assign({}, m); n[item.cheie] = e.target.value; return n; }); }
                },
                React.createElement('option', { value: 'true' }, 'Activ — utilizatorii trebuie să introducă codul OTP la login'),
                React.createElement('option', { value: 'false' }, 'Inactiv — login fără cod OTP')
              )
              : React.createElement('input', {
                  style: s.inp,
                  defaultValue: item.valoare || '',
                  onChange: function(e) { setModificate(function(m) { var n = Object.assign({}, m); n[item.cheie] = e.target.value; return n; }); }
                })
          );
        })
      )
    ),
    Object.entries(grupe).map(function(entry) {
      var grupaLabel = entry[0]; var chei = entry[1];
      return React.createElement(Card, { key: grupaLabel, title: grupaLabel },
        React.createElement('div', { style: s.formGrid },
          chei.map(function(cheie) {
            var item = (data || []).find(function(d) { return d.cheie === cheie; });
            if (!item) return null;
            var isBoolItem = cheiBool.includes(cheie);
            var valCurentaItem = modificate[cheie] !== undefined ? modificate[cheie] : item.valoare;
            return React.createElement('div', { key: cheie, style: s.fld },
              React.createElement('label', { style: s.lbl }, item.descriere || cheie),
              isBoolItem
                ? React.createElement('select', {
                    style: s.inp,
                    value: valCurentaItem,
                    onChange: function(e) { setModificate(function(m) { var n = Object.assign({}, m); n[cheie] = e.target.value; return n; }); }
                  },
                  React.createElement('option', { value: 'true' }, 'Activ'),
                  React.createElement('option', { value: 'false' }, 'Inactiv')
                )
                : cheie.includes('PASS')
                  ? React.createElement(PasswordInput, {
                      style: s.inp,
                      autoComplete: 'off',
                      defaultValue: item.valoare || '',
                      placeholder: item.valoare === '••••••••' ? 'Lasă gol pentru a păstra' : '',
                      onChange: function(e) { setModificate(function(m) { var n = Object.assign({}, m); n[cheie] = e.target.value; return n; }); }
                    })
                  : React.createElement('input', {
                      style: s.inp,
                      type: 'text',
                      defaultValue: item.valoare || '',
                      placeholder: item.valoare === '••••••••' ? 'Lasă gol pentru a păstra' : '',
                      onChange: function(e) { setModificate(function(m) { var n = Object.assign({}, m); n[cheie] = e.target.value; return n; }); }
                    })
            );
          })
        )
      );
    }),
    Object.keys(modificate).length > 0 && React.createElement('div', {
      style: { position: 'sticky', bottom: '20px', backgroundColor: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
    },
      React.createElement('span', { style: { fontSize: '14px', color: '#555' } }, Object.keys(modificate).length + ' modificări nesalvate'),
      React.createElement('div', { style: { display: 'flex', gap: '8px' } },
        React.createElement(Button, { onClick: function() { setModificate({}); }, variant: 'ghost' }, 'Anulează'),
        React.createElement(Button, { onClick: function() { saveMut.mutate(); }, disabled: saveMut.isLoading }, saveMut.isLoading ? 'Se salvează...' : 'Salvează toate')
      )
    )
  );
}

function TabAudit() {
  var [filters, setFilters] = useState({ actiune: '', email: '', data_start: '', data_end: '', ip: '' });
  var [applied, setApplied] = useState({});
  var [page, setPage] = useState(1);
  var [exporting, setExporting] = useState(false);

  var { data: result, isLoading } = useQuery(
    ['admin-audit', applied, page],
    function() { return adminAPI.getAudit(Object.assign({}, applied, { page: page })); },
    { keepPreviousData: true }
  );

  var rows = (result && result.data) || [];
  var total = (result && result.total) || 0;
  var totalPages = Math.max(1, Math.ceil(total / 50));

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    var active = {};
    Object.keys(filters).forEach(function(k) { if (filters[k]) active[k] = filters[k]; });
    setApplied(active);
  }

  function resetFilters() {
    setFilters({ actiune: '', email: '', data_start: '', data_end: '', ip: '' });
    setApplied({});
    setPage(1);
  }

  async function handleExport() {
    setExporting(true);
    try {
      var r = await adminAPI.exportAudit(applied);
      var url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      var a = document.createElement('a');
      a.href = url;
      a.download = 'audit-civicapp-' + new Date().toISOString().split('T')[0] + '.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch(err) { toast.error('Eroare la export'); }
    finally { setExporting(false); }
  }

  function upd(field) {
    return function(e) { setFilters(function(f) { return Object.assign({}, f, { [field]: e.target.value }); }); };
  }

  var finp = { style: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' } };
  var flbl = { style: { fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' } };

  return React.createElement(Card, { title: 'Audit log' },
    // Filtru
    React.createElement('form', {
      onSubmit: applyFilters,
      style: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end', marginBottom: '16px', padding: '14px', backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }
    },
      React.createElement('div', { style: { flex: '1 1 150px' } },
        React.createElement('label', flbl, 'Acțiune'),
        React.createElement('input', Object.assign({}, finp, { placeholder: 'ex: LOGIN_OK', value: filters.actiune, onChange: upd('actiune') }))
      ),
      React.createElement('div', { style: { flex: '1 1 170px' } },
        React.createElement('label', flbl, 'Email utilizator'),
        React.createElement('input', Object.assign({}, finp, { placeholder: 'parțial...', value: filters.email, onChange: upd('email') }))
      ),
      React.createElement('div', { style: { flex: '1 1 130px' } },
        React.createElement('label', flbl, 'De la'),
        React.createElement('input', Object.assign({}, finp, { type: 'date', value: filters.data_start, onChange: upd('data_start') }))
      ),
      React.createElement('div', { style: { flex: '1 1 130px' } },
        React.createElement('label', flbl, 'Până la'),
        React.createElement('input', Object.assign({}, finp, { type: 'date', value: filters.data_end, onChange: upd('data_end') }))
      ),
      React.createElement('div', { style: { flex: '1 1 110px' } },
        React.createElement('label', flbl, 'IP'),
        React.createElement('input', Object.assign({}, finp, { placeholder: '192.168...', value: filters.ip, onChange: upd('ip') }))
      ),
      React.createElement('div', { style: { display: 'flex', gap: '8px' } },
        React.createElement(Button, { type: 'submit' }, 'Filtrează'),
        React.createElement(Button, { type: 'button', variant: 'ghost', onClick: resetFilters }, 'Reset'),
        React.createElement(Button, { type: 'button', variant: 'secondary', disabled: exporting, onClick: handleExport },
          exporting ? 'Export...' : '↓ CSV'
        )
      )
    ),

    // Contor
    React.createElement('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '10px' } },
      total + ' înregistrări' + (Object.keys(applied).length ? ' (filtrate)' : ' total')
    ),

    isLoading && React.createElement(Spinner, null),

    // Tabel
    !isLoading && React.createElement('div', { style: { overflowX: 'auto' } },
      React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' } },
        React.createElement('thead', null,
          React.createElement('tr', { style: { borderBottom: '2px solid #e5e7eb' } },
            ['Data/Ora', 'Acțiune', 'Utilizator', 'IP', 'Detalii'].map(function(h) {
              return React.createElement('th', { key: h, style: { padding: '8px 10px', color: '#6b7280', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' } }, h);
            })
          )
        ),
        React.createElement('tbody', null,
          rows.map(function(a, i) {
            var userName = ((a.prenume || '') + ' ' + (a.nume || '')).trim() || 'Sistem';
            return React.createElement('tr', { key: a.id || i, style: { borderBottom: '1px solid #f3f4f6' } },
              React.createElement('td', { style: { padding: '7px 10px', color: '#9ca3af', whiteSpace: 'nowrap', fontSize: '12px' } }, formatDataOra(a.created_at)),
              React.createElement('td', { style: { padding: '7px 10px' } },
                React.createElement('span', { style: { fontFamily: 'monospace', fontSize: '11px', backgroundColor: '#eff6ff', color: '#1d4ed8', padding: '2px 6px', borderRadius: '4px' } }, a.actiune)
              ),
              React.createElement('td', { style: { padding: '7px 10px' } },
                React.createElement('div', { style: { color: '#374151' } }, userName),
                a.email && React.createElement('div', { style: { fontSize: '11px', color: '#9ca3af' } }, a.email)
              ),
              React.createElement('td', { style: { padding: '7px 10px', color: '#9ca3af', fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'nowrap' } }, a.ip_address || '—'),
              React.createElement('td', { style: { padding: '7px 10px', color: '#6b7280', fontFamily: 'monospace', fontSize: '11px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                a.detalii ? JSON.stringify(a.detalii) : '—'
              )
            );
          })
        )
      )
    ),

    // Paginare
    totalPages > 1 && React.createElement('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px', justifyContent: 'center' } },
      React.createElement(Button, { variant: 'ghost', disabled: page <= 1, onClick: function() { setPage(function(p) { return p - 1; }); } }, '← Anterior'),
      React.createElement('span', { style: { fontSize: '13px', color: '#6b7280' } }, 'Pagina ' + page + ' din ' + totalPages),
      React.createElement(Button, { variant: 'ghost', disabled: page >= totalPages, onClick: function() { setPage(function(p) { return p + 1; }); } }, 'Următor →')
    )
  );
}

function TabLicenta() {
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const { data: status, isLoading } = useQuery('license-status', adminAPI.getLicenseStatus);

  const saveMut = useMutation(adminAPI.saveLicenseToken, {
    onSuccess: function(data) {
      queryClient.setQueryData('license-status', data);
      setToken('');
      toast.success(data.valid ? 'Licență salvată și validă!' : 'Licență salvată, dar invalidă: ' + data.reason);
    },
    onError: function(e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare salvare'); },
  });
  const recheckMut = useMutation(adminAPI.recheckLicense, {
    onSuccess: function(data) {
      queryClient.setQueryData('license-status', data);
      toast.success('Verificat (inclusiv online, dacă serverul de licențe e accesibil).');
    },
    onError: function() { toast.error('Eroare la reverificare'); },
  });

  if (isLoading) return React.createElement(Spinner);

  const valid = status && status.valid;
  const expirareCuranda = status && status.daysLeft !== null && status.daysLeft <= 60 && !status.lifetime;

  return React.createElement(React.Fragment, null,
    React.createElement(Card, { title: 'Stare licență' },
      React.createElement('div', {
        style: {
          padding: '20px', borderRadius: '12px', marginBottom: '20px',
          backgroundColor: valid ? '#f0fdf4' : '#fef2f2',
          border: '2px solid ' + (valid ? '#16a34a' : '#dc2626'),
        }
      },
        React.createElement('p', { style: { margin: 0, fontSize: '16px', fontWeight: '700', color: valid ? '#166534' : '#991b1b' } },
          valid ? '✓ Licență activă' : '✗ Licență invalidă' + (status && status.reason ? ' — ' + status.reason : '')
        ),
        status && status.client && React.createElement('p', { style: { margin: '8px 0 0', fontSize: '14px', color: '#444' } }, 'Titular: ' + status.client),
        status && valid && React.createElement('p', { style: { margin: '4px 0 0', fontSize: '14px', color: '#444' } },
          status.lifetime ? 'Valabilă pe viață' : ('Expiră la ' + new Date(status.expiresAt).toLocaleDateString('ro-RO') + ' (' + status.daysLeft + ' zile rămase)')
        ),
        expirareCuranda && React.createElement('p', { style: { margin: '8px 0 0', fontSize: '13px', color: '#b45309', fontWeight: '600' } }, '⚠ Licența expiră în curând — contactează furnizorul pentru reînnoire.'),
        status && React.createElement('p', { style: { margin: '12px 0 0', fontSize: '12px', color: '#888' } },
          'Sursă verificare: ' + (status.source === 'online+offline' ? 'offline + online' : 'doar offline') +
          (status.lastOnlineCheck ? (' · ultima verificare online: ' + new Date(status.lastOnlineCheck).toLocaleString('ro-RO')) : '')
        )
      ),
      React.createElement(Button, { variant: 'ghost', onClick: function() { recheckMut.mutate(); }, disabled: recheckMut.isLoading },
        recheckMut.isLoading ? 'Se verifică...' : 'Reverifică acum (online)'
      )
    ),
    React.createElement(Card, { title: 'Introdu token de licență' },
      React.createElement('p', { style: { fontSize: '14px', color: '#666', marginBottom: '16px' } },
        'Lipește aici tokenul de licență primit de la furnizor (emis din License Server).'
      ),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '600px' } },
        React.createElement('textarea', {
          style: Object.assign({}, s.inp, { minHeight: '90px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }),
          value: token,
          onChange: function(e) { setToken(e.target.value); },
          placeholder: 'ex: eyJsaWMiOiJ...==.MEUCIQD...=='
        }),
        React.createElement(Button, {
          onClick: function() { saveMut.mutate(token); },
          disabled: !token.trim() || saveMut.isLoading
        }, saveMut.isLoading ? 'Se salvează...' : 'Salvează licența')
      )
    )
  );
}

function TabEmailSMTP() {
  const [cfg, setCfg] = useState({ SMTP_HOST: '', SMTP_PORT: '587', SMTP_SECURE: 'false', SMTP_USER: '', SMTP_PASS: '', SMTP_FROM: '', SMTP_FROM_NAME: '' });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  React.useEffect(function() {
    adminAPI.getSmtpConfig().then(function(c) { setCfg(function(prev) { return Object.assign({}, prev, c); }); setLoaded(true); })
      .catch(function() { setLoaded(true); });
  }, []);

  const set = function(k, v) { setCfg(function(c) { return Object.assign({}, c, { [k]: v }); }); };

  const save = function() {
    if (!cfg.SMTP_HOST || !cfg.SMTP_USER || !cfg.SMTP_FROM) { toast.error('Completează host, utilizator și adresă expeditor.'); return; }
    setSaving(true);
    adminAPI.saveSmtpConfig(cfg)
      .then(function() { toast.success('Configurare SMTP salvată.'); })
      .catch(function(e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare salvare'); })
      .finally(function() { setSaving(false); });
  };

  const test = function() {
    setTesting(true);
    adminAPI.testSmtp()
      .then(function(r) { toast.success(r.message || 'Email de test trimis!'); })
      .catch(function(e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare test conexiune'); })
      .finally(function() { setTesting(false); });
  };

  if (!loaded) return React.createElement(Spinner);

  return React.createElement(Card, { title: 'Email & SMTP' },
    React.createElement('p', { style: { fontSize: '14px', color: '#666', marginBottom: '20px' } },
      'Configurare server de email pentru notificări (confirmare cerere, resetare parolă, cod OTP etc.)'
    ),
    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '500px' } },
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 120px', gap: '12px' } },
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Server SMTP (host) *'),
          React.createElement('input', { style: s.inp, value: cfg.SMTP_HOST, placeholder: 'smtp.gmail.com', onChange: function(e) { set('SMTP_HOST', e.target.value); } })
        ),
        React.createElement('div', { style: s.fld },
          React.createElement('label', { style: s.lbl }, 'Port'),
          React.createElement('input', { style: s.inp, type: 'number', value: cfg.SMTP_PORT, onChange: function(e) { set('SMTP_PORT', e.target.value); } })
        )
      ),
      React.createElement('div', { style: s.fld },
        React.createElement('label', { style: s.lbl }, 'Utilizator SMTP *'),
        React.createElement('input', { style: s.inp, value: cfg.SMTP_USER, placeholder: 'admin@exemplu.ro', onChange: function(e) { set('SMTP_USER', e.target.value); } })
      ),
      React.createElement('div', { style: s.fld },
        React.createElement('label', { style: s.lbl }, 'Parolă / cheie de aplicație'),
        React.createElement('input', { style: s.inp, type: 'password', value: cfg.SMTP_PASS, placeholder: 'Lasă gol pentru a păstra parola existentă', onChange: function(e) { set('SMTP_PASS', e.target.value); } })
      ),
      React.createElement('div', { style: s.fld },
        React.createElement('label', { style: s.lbl }, 'Adresă expeditor (From) *'),
        React.createElement('input', { style: s.inp, value: cfg.SMTP_FROM, placeholder: 'admin@exemplu.ro', onChange: function(e) { set('SMTP_FROM', e.target.value); } })
      ),
      React.createElement('div', { style: s.fld },
        React.createElement('label', { style: s.lbl }, 'Nume expeditor'),
        React.createElement('input', { style: s.inp, value: cfg.SMTP_FROM_NAME, placeholder: 'Primăria Orașului', onChange: function(e) { set('SMTP_FROM_NAME', e.target.value); } })
      ),
      React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' } },
        React.createElement('input', { type: 'checkbox', checked: cfg.SMTP_SECURE === 'true', onChange: function(e) { set('SMTP_SECURE', e.target.checked ? 'true' : 'false'); } }),
        'Conexiune SSL/TLS (bifează pentru port 465; lasă nebifat pentru STARTTLS/587)'
      ),
      React.createElement('div', { style: { display: 'flex', gap: '10px', flexWrap: 'wrap' } },
        React.createElement(Button, { onClick: save, disabled: saving }, saving ? 'Se salvează...' : 'Salvează configurarea'),
        React.createElement(Button, { variant: 'ghost', onClick: test, disabled: testing || !cfg.SMTP_HOST }, testing ? 'Se trimite...' : 'Trimite email de test')
      )
    )
  );
}

function TabComunicare() {
  const [titlu, setTitlu] = useState('');
  const [mesaj, setMesaj] = useState('');
  const [rezultat, setRezultat] = useState(null);
  const broadcastMut = useMutation(adminAPI.broadcast, {
    onSuccess: function(r) {
      setRezultat(r.data);
      setTitlu('');
      setMesaj('');
    },
    onError: function(e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Eroare trimitere'); },
  });

  return React.createElement(Card, { title: 'Comunicare cetățeni' },
    React.createElement('p', { style: { fontSize: '14px', color: '#666', marginBottom: '20px' } },
      'Trimite o notificare în aplicație și pe email tuturor cetățenilor cu cont activ.'
    ),
    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '600px' } },
      React.createElement('div', { style: s.fld },
        React.createElement('label', { style: s.lbl }, 'Titlu notificare *'),
        React.createElement('input', {
          style: s.inp, value: titlu, maxLength: 200,
          onChange: function(e) { setTitlu(e.target.value); setRezultat(null); },
          placeholder: 'ex: Aviz public — consultare urbanism'
        })
      ),
      React.createElement('div', { style: s.fld },
        React.createElement('label', { style: s.lbl }, 'Mesaj *'),
        React.createElement('textarea', {
          style: Object.assign({}, s.inp, { minHeight: '120px', resize: 'vertical', fontFamily: 'inherit' }),
          value: mesaj,
          onChange: function(e) { setMesaj(e.target.value); setRezultat(null); },
          placeholder: 'Textul mesajului care va fi trimis cetățenilor...'
        })
      ),
      React.createElement(Button, {
        onClick: function() {
          if (!titlu.trim() || !mesaj.trim()) { toast.error('Completați titlul și mesajul'); return; }
          if (!window.confirm('Trimiteți notificarea la toți cetățenii activi?')) return;
          broadcastMut.mutate({ titlu: titlu.trim(), mesaj: mesaj.trim() });
        },
        disabled: broadcastMut.isLoading
      }, broadcastMut.isLoading ? 'Se trimite...' : 'Trimite la toți cetățenii'),
      rezultat && React.createElement('div', {
        style: { padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '10px', border: '1.5px solid #86efac', fontSize: '14px', color: '#166534' }
      },
        React.createElement('strong', null, '✓ Notificare trimisă'),
        React.createElement('br', null),
        'Destinatari: ' + rezultat.trimisi + ' cetățeni',
        React.createElement('br', null),
        'Email-uri trimise: ' + rezultat.emailOk + (rezultat.emailErr > 0 ? ' (' + rezultat.emailErr + ' erori)' : '')
      )
    )
  );
}

// ── Configurare pași disponibili ──────────────────────────────
const ETAPE_DISPONIBILE = [
  { etapa: 'inregistrata',       denumire: 'Înregistrare',              rol_default: 'functionar',       icon: '📥' },
  { etapa: 'repartizata',        denumire: 'Repartizare',               rol_default: 'functionar',       icon: '📋' },
  { etapa: 'in_lucru',           denumire: 'Analiză / În lucru',        rol_default: 'functionar',       icon: '⚙️' },
  { etapa: 'spre_aprobare',      denumire: 'Trimis spre aprobare',      rol_default: 'functionar',       icon: '👁' },
  { etapa: 'aprobata',           denumire: 'Aprobare',                  rol_default: 'sef_departament',  icon: '✓' },
  { etapa: 'respinsa',           denumire: 'Respingere',                rol_default: 'sef_departament',  icon: '✗' },
  { etapa: 'spre_contrasemnare', denumire: 'Contrasemnare secretar',    rol_default: 'secretar_general', icon: '⚖️' },
  { etapa: 'contrasemnata',      denumire: 'Contrasemnat',              rol_default: 'secretar_general', icon: '✍' },
  { etapa: 'spre_semnare',       denumire: 'Trimis spre semnare',       rol_default: 'sef_departament',  icon: '✍️' },
  { etapa: 'semnata',            denumire: 'Semnat de primar',          rol_default: 'primar',           icon: '🖊' },
  { etapa: 'arhivata',           denumire: 'Arhivare',                  rol_default: 'sef_departament',  icon: '📁' },
];

const ROLURI_FLUX = [
  { value: 'functionar',       label: 'Funcționar' },
  { value: 'sef_departament',  label: 'Șef departament' },
  { value: 'secretar_general', label: 'Secretar General' },
  { value: 'primar',           label: 'Primar' },
];

function TabFluxuri() {
  const qc = useQueryClient();
  const [editId, setEditId] = useState(null); // null=lista, 'new'=nou, uuid=editare

  const { data: workflows, isLoading } = useQuery('admin-workflows', adminAPI.getWorkflows);

  const toggleMut = useMutation(adminAPI.toggleWorkflow, {
    onSuccess: () => { toast.success('Status actualizat'); qc.invalidateQueries('admin-workflows'); },
    onError: () => toast.error('Eroare'),
  });
  const setImplicitMut = useMutation(adminAPI.setImplicit, {
    onSuccess: () => { toast.success('Template setat ca implicit (fallback)'); qc.invalidateQueries('admin-workflows'); },
    onError: () => toast.error('Eroare'),
  });
  const deleteMut = useMutation(adminAPI.deleteWorkflow, {
    onSuccess: () => { toast.success('Flux șters'); qc.invalidateQueries('admin-workflows'); },
    onError: (e) => toast.error(e.response?.data?.message || 'Eroare'),
  });

  if (editId !== null) {
    return (
      <WorkflowEditor
        templateId={editId}
        onBack={() => setEditId(null)}
        onSaved={() => { qc.invalidateQueries('admin-workflows'); setEditId(null); }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Configurați fluxurile de documente pentru fiecare tip de cerere. Puteți adăuga, reordona și personaliza pașii prin drag & drop.
          </p>
        </div>
        <Button onClick={() => setEditId('new')}>+ Flux nou</Button>
      </div>

      {isLoading ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {(workflows || []).length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px', color: '#888' }}>
              Niciun flux configurat. Apăsați "+ Flux nou" pentru a crea primul.
            </div>
          )}
          {(workflows || []).map(wf => (
            <div key={wf.id} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 16px', background: '#fff', borderRadius: '10px',
              border: wf.implicit ? '2px solid #2d2d7e' : '1.5px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>{wf.denumire}</span>
                  {wf.implicit && <span style={{ fontSize: '10px', background: '#eef2ff', color: '#2d2d7e', padding: '2px 7px', borderRadius: '10px', fontWeight: '600' }}>IMPLICIT</span>}
                  {!wf.activ  && <span style={{ fontSize: '10px', background: '#fee2e2', color: '#dc2626', padding: '2px 7px', borderRadius: '10px' }}>INACTIV</span>}
                </div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '3px' }}>
                  Cod: <code style={{ background: '#f5f5f5', padding: '1px 5px', borderRadius: '4px' }}>{wf.cod}</code>
                  {wf.tip_cerere && <> · Tip: {wf.tip_cerere}</>}
                  {' '}· {wf.numar_pasi} pași
                  {wf.creat_de_nume && <> · Creat de {wf.creat_de_nume}</>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button style={swf.btn} onClick={() => setEditId(wf.id)}>✏️ Editează</button>
                {!wf.implicit && (
                  <button style={{ ...swf.btn, background: '#eef2ff', color: '#2d2d7e', border: '1.5px solid #c7d2fe' }}
                    onClick={() => { if (window.confirm('Setezi "' + wf.denumire + '" ca flux implicit (fallback)?')) setImplicitMut.mutate(wf.id); }}>
                    ⭐ Setează implicit
                  </button>
                )}
                <button style={{ ...swf.btn, background: wf.activ ? '#fef3c7' : '#f0fdf4', color: wf.activ ? '#92400e' : '#166534' }}
                  onClick={() => toggleMut.mutate(wf.id)}>
                  {wf.activ ? 'Dezactivează' : 'Activează'}
                </button>
                <button style={{ ...swf.btn, background: '#fee2e2', color: '#dc2626' }}
                  onClick={() => { if (window.confirm('Ștergi fluxul "' + wf.denumire + '"?')) deleteMut.mutate(wf.id); }}>
                  Șterge
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkflowEditor({ templateId, onBack, onSaved }) {
  const isNew = templateId === 'new';
  const { data: categorii } = useQuery('admin-categorii', adminAPI.getCategorii);
  const [form, setForm]     = useState({ cod: '', denumire: '', descriere: '', tip_cerere: '', activ: true, implicit: false });
  const [pasi, setPasi]     = useState([]);
  const [loadingData, setLoadingData] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    if (isNew) {
      setPasi([
        { etapa: 'inregistrata',  denumire: 'Înregistrare',       rol_permis: 'functionar',      obligatoriu: true },
        { etapa: 'repartizata',   denumire: 'Repartizare',        rol_permis: 'functionar',      obligatoriu: true },
        { etapa: 'in_lucru',      denumire: 'Analiză',            rol_permis: 'functionar',      obligatoriu: true },
        { etapa: 'spre_aprobare', denumire: 'Aprobare șef',       rol_permis: 'sef_departament', obligatoriu: true },
        { etapa: 'aprobata',      denumire: 'Aprobat',            rol_permis: 'sef_departament', obligatoriu: true },
        { etapa: 'arhivata',      denumire: 'Arhivare',           rol_permis: 'sef_departament', obligatoriu: true },
      ]);
    } else {
      adminAPI.getWorkflow(templateId).then(data => {
        setForm({
          cod:         data.template.cod,
          denumire:    data.template.denumire,
          descriere:   data.template.descriere || '',
          tip_cerere:  data.template.tip_cerere || '',
          categorie_id: data.template.categorie_id || '',
          activ:       data.template.activ,
          implicit:    data.template.implicit,
        });
        setPasi(data.pasi.map(p => ({
          etapa:      p.etapa,
          denumire:   p.denumire,
          rol_permis: p.rol_permis || '',
          obligatoriu: p.obligatoriu,
          descriere:   p.descriere || '',
        })));
        setLoadingData(false);
      }).catch(() => { toast.error('Eroare la încărcare'); setLoadingData(false); });
    }
  }, [templateId]);

  // ── Drag & Drop (HTML5 nativ) ─────────────────────────────
  function onDragStart(idx) { setDragIdx(idx); }
  function onDragOver(e, idx) { e.preventDefault(); setOverIdx(idx); }
  function onDrop(idx) {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...pasi];
    const [removed] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, removed);
    setPasi(arr);
    setDragIdx(null);
    setOverIdx(null);
  }
  function onDragEnd() { setDragIdx(null); setOverIdx(null); }

  function addPas(etapa) {
    const cfg = ETAPE_DISPONIBILE.find(e => e.etapa === etapa);
    if (!cfg) return;
    setPasi(prev => [...prev, { etapa, denumire: cfg.denumire, rol_permis: cfg.rol_default, obligatoriu: true, descriere: '' }]);
  }

  function updatePas(idx, key, val) {
    setPasi(prev => prev.map((p, i) => i === idx ? { ...p, [key]: val } : p));
  }

  function movePas(idx, dir) {
    const arr = [...pasi];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    setPasi(arr);
  }

  async function save() {
    if (!form.cod && isNew) return toast.error('Codul fluxului este obligatoriu');
    if (!form.denumire) return toast.error('Denumirea este obligatorie');
    if (pasi.length === 0) return toast.error('Adăugați cel puțin un pas');
    setSaving(true);
    try {
      const payload = { ...form, pasi };
      if (isNew) await adminAPI.createWorkflow(payload);
      else       await adminAPI.updateWorkflow(templateId, payload);
      toast.success('Flux salvat cu succes!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  }

  if (loadingData) return <Spinner message="Se încarcă fluxul..." />;

  const etapeAdaugate = new Set(pasi.map(p => p.etapa));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#6b7280', padding: '6px 0' }}>
          ← Înapoi
        </button>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>
          {isNew ? 'Flux nou de documente' : `Editare: ${form.denumire}`}
        </h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '20px', alignItems: 'start' }}>

        {/* Coloana stânga — informații flux */}
        <div>
          <Card title="Informații flux">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={swf.fld}>
                <label style={swf.lbl}>Denumire flux *</label>
                <input style={swf.inp} value={form.denumire} onChange={e => setF('denumire', e.target.value)}
                  placeholder="ex: Autorizație construire" />
              </div>
              {isNew && (
                <div style={swf.fld}>
                  <label style={swf.lbl}>Cod unic * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(litere mici, cifre, -)</span></label>
                  <input style={swf.inp} value={form.cod}
                    onChange={e => setF('cod', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    placeholder="ex: autorizatie-construire" />
                </div>
              )}
              {!isNew && (
                <div style={swf.fld}>
                  <label style={swf.lbl}>Cod</label>
                  <code style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: '8px', fontSize: '13px' }}>{form.cod}</code>
                </div>
              )}
              <div style={swf.fld}>
                <label style={swf.lbl}>Tip cerere asociat</label>
                <select style={swf.inp} value={form.tip_cerere || ''} onChange={e => setF('tip_cerere', e.target.value)}>
                  <option value="">Toate tipurile</option>
                  <option value="sesizare">Sesizare</option>
                  <option value="solicitare">Solicitare</option>
                  <option value="petitie">Petiție</option>
                  <option value="reclamatie">Reclamație</option>
                  <option value="informatie">Cerere de informații</option>
                </select>
              </div>
              <div style={swf.fld}>
                <label style={swf.lbl}>Categorie cerere</label>
                <select style={swf.inp} value={form.categorie_id || ''} onChange={e => setF('categorie_id', e.target.value)}>
                  <option value="">Toate categoriile</option>
                  {(categorii || []).map(c => <option key={c.id} value={c.id}>{c.nume}</option>)}
                </select>
              </div>
              <div style={swf.fld}>
                <label style={swf.lbl}>Descriere</label>
                <textarea style={{ ...swf.inp, resize: 'vertical', minHeight: '70px', fontFamily: 'inherit' }}
                  value={form.descriere} onChange={e => setF('descriere', e.target.value)}
                  placeholder="Note despre când se aplică acest flux..." />
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.activ} onChange={e => setF('activ', e.target.checked)} />
                  Flux activ
                </label>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.implicit} onChange={e => setF('implicit', e.target.checked)} />
                  Flux implicit (fallback)
                </label>
              </div>
            </div>
          </Card>

          {/* Adaugă pas nou */}
          <Card title="Adaugă pas în flux">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ETAPE_DISPONIBILE.filter(e => !etapeAdaugate.has(e.etapa)).map(et => (
                <button key={et.etapa} onClick={() => addPas(et.etapa)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1.5px dashed #d1d5db', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', textAlign: 'left', width: '100%', transition: 'all .15s' }}
                  onMouseOver={e => e.currentTarget.style.borderColor = '#2d2d7e'}
                  onMouseOut={e => e.currentTarget.style.borderColor = '#d1d5db'}>
                  <span style={{ fontSize: '18px' }}>{et.icon}</span>
                  <div>
                    <div style={{ fontWeight: '500' }}>{et.denumire}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>Rol implicit: {et.rol_default}</div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: '#9ca3af' }}>+</span>
                </button>
              ))}
              {etapeAdaugate.size === ETAPE_DISPONIBILE.length && (
                <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '13px', margin: 0 }}>Toate etapele au fost adăugate.</p>
              )}
            </div>
          </Card>
        </div>

        {/* Coloana dreapta — editor pași cu drag & drop */}
        <div>
          <Card title={`Pași flux (${pasi.length}) — trageți pentru a reordona`}
            action={
              <Button onClick={save} disabled={saving}>
                {saving ? 'Se salvează...' : '💾 Salvează fluxul'}
              </Button>
            }>
            {pasi.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', fontSize: '14px' }}>
                Niciun pas adăugat. Selectați pașii din stânga.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {pasi.map((pas, idx) => {
                  const etapaCfg = ETAPE_DISPONIBILE.find(e => e.etapa === pas.etapa);
                  const isDragging = dragIdx === idx;
                  const isOver = overIdx === idx && dragIdx !== null && dragIdx !== idx;
                  return (
                    <div
                      key={`${pas.etapa}-${idx}`}
                      draggable
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={e => onDragOver(e, idx)}
                      onDrop={() => onDrop(idx)}
                      onDragEnd={onDragEnd}
                      style={{
                        background: isDragging ? '#eef2ff' : isOver ? '#f0fdf4' : '#fafafa',
                        border: isOver ? '2px solid #10b981' : isDragging ? '2px dashed #2d2d7e' : '1.5px solid #e5e7eb',
                        borderRadius: '10px',
                        padding: '12px',
                        opacity: isDragging ? 0.6 : 1,
                        transition: 'background .1s, border .1s',
                      }}>
                      {/* Header pas */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ cursor: 'grab', fontSize: '16px', color: '#9ca3af', userSelect: 'none', touchAction: 'none' }}>⠿⠿</span>
                        <span style={{ width: '24px', height: '24px', background: '#2d2d7e', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>{idx + 1}</span>
                        <span style={{ fontSize: '16px' }}>{etapaCfg?.icon || '▸'}</span>
                        <span style={{ flex: 1, fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>{pas.etapa}</span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => movePas(idx, -1)} disabled={idx === 0}
                            style={{ ...swf.btnIcon, opacity: idx === 0 ? 0.3 : 1 }} title="Sus">↑</button>
                          <button onClick={() => movePas(idx, 1)} disabled={idx === pasi.length - 1}
                            style={{ ...swf.btnIcon, opacity: idx === pasi.length - 1 ? 0.3 : 1 }} title="Jos">↓</button>
                          <button onClick={() => setPasi(prev => prev.filter((_, i) => i !== idx))}
                            style={{ ...swf.btnIcon, background: '#fee2e2', color: '#dc2626' }} title="Elimină">✕</button>
                        </div>
                      </div>

                      {/* Câmpuri editare */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={swf.fld}>
                          <label style={swf.lbl}>Etichetă pas</label>
                          <input style={swf.inpS} value={pas.denumire}
                            onChange={e => updatePas(idx, 'denumire', e.target.value)} />
                        </div>
                        <div style={swf.fld}>
                          <label style={swf.lbl}>Rol executor</label>
                          <select style={swf.inpS} value={pas.rol_permis}
                            onChange={e => updatePas(idx, 'rol_permis', e.target.value)}>
                            <option value="">Orice rol</option>
                            {ROLURI_FLUX.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '16px', marginTop: '8px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', cursor: 'pointer', color: '#555' }}>
                          <input type="checkbox" checked={pas.obligatoriu}
                            onChange={e => updatePas(idx, 'obligatoriu', e.target.checked)} />
                          Pas obligatoriu
                        </label>
                        <input style={{ ...swf.inpS, flex: 1, fontSize: '12px' }} value={pas.descriere || ''}
                          onChange={e => updatePas(idx, 'descriere', e.target.value)}
                          placeholder="Notă (opțional)" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {pasi.length > 0 && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={save} disabled={saving}>
                  {saving ? 'Se salvează...' : '💾 Salvează fluxul'}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── TabImpozite ───────────────────────────────────────────────
const TIP_PROP = [
  { value: 'cladire',   label: 'Clădire',                icon: '🏠' },
  { value: 'teren',     label: 'Teren',                  icon: '🌱' },
  { value: 'transport', label: 'Mijloc de transport',    icon: '🚗' },
];
const CATEGORII_TEREN = [
  'intravilan_curti', 'intravilan_agricol', 'extravilan_arabil',
  'extravilan_pasuni', 'extravilan_paduri', 'extravilan_ape', 'extravilan_neproductiv',
];
const TIP_TRANSPORT_OPT = ['autoturism', 'motocicleta', 'camion', 'autobuz', 'remorca', 'altul'];
const METODE_PLATA = ['numerar', 'card', 'transfer', 'online'];

function fmtL(n) {
  if (n == null) return '—';
  return parseFloat(n).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' lei';
}
function fmtD(d) { return d ? new Date(d).toLocaleDateString('ro-RO') : '—'; }

function TabImpozite() {
  const qc = useQueryClient();
  const anCurent = new Date().getFullYear();
  const [an, setAn]               = useState(anCurent);
  const [cautare, setCautare]     = useState('');
  const [cetatean, setCetatean]   = useState(null);
  const [rezultate, setRezultate] = useState([]);
  const [loadSearch, setLoadSearch]   = useState(false);
  const [importing, setImporting]     = useState(false);
  const [importRez, setImportRez]     = useState(null); // { importate, actualizate, erori }
  const fileInputRef = React.useRef();

  // Modals
  const [modalProp, setModalProp]   = useState(null);
  const [modalImpoz, setModalImpoz] = useState(null);
  const [modalPlata, setModalPlata] = useState(null);

  const { data: proprietati, isLoading: loadProps } = useQuery(
    ['admin-proprietati', cetatean?.id],
    () => adminAPI.getProprietati(cetatean.id),
    { enabled: !!cetatean }
  );
  const { data: impozite, isLoading: loadImpoz } = useQuery(
    ['admin-impozite', cetatean?.id, an],
    () => adminAPI.getImpozite(cetatean.id, an),
    { enabled: !!cetatean }
  );

  const refetch = () => {
    qc.invalidateQueries(['admin-proprietati', cetatean?.id]);
    qc.invalidateQueries(['admin-impozite', cetatean?.id, an]);
  };

  async function onSearch(e) {
    e.preventDefault();
    if (cautare.length < 2) return;
    setLoadSearch(true);
    try {
      const data = await adminAPI.cautaUtilizatori(cautare);
      setRezultate(data || []);
    } catch { toast.error('Eroare la căutare'); }
    finally { setLoadSearch(false); }
  }

  function selectCetatean(u) { setCetatean(u); setRezultate([]); }

  function doExport() {
    // Navigăm direct — browserul va descărca fișierul
    const token = getAccessToken();
    const url = `/api/impozite/export?an=${an}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `impozite_${an}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Eroare la export'));
  }

  async function doImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const token = getAccessToken();
      const fd = new FormData();
      fd.append('fisier', file);
      const r = await fetch('/api/impozite/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await r.json();
      if (json.success) {
        setImportRez(json.data);
        qc.invalidateQueries(['admin-proprietati']);
        qc.invalidateQueries(['admin-impozite']);
      } else {
        toast.error(json.message || 'Eroare la import');
      }
    } catch { toast.error('Eroare la import'); }
    finally { setImporting(false); }
  }

  // Impozit map: proprietate_id -> impozit row
  const impozitMap = {};
  for (const i of (impozite || [])) impozitMap[i.id] = i;
  // Flat array indexed by id
  const impozitByProp = {};
  for (const i of (impozite || [])) impozitByProp[i.id] = i;
  // Actually impozite list has p.*, i.* joined — let me separate
  const impMap = {};
  for (const row of (impozite || [])) {
    if (row.impozit_id) impMap[row.id] = row; // proprietate id -> row with impozit data
  }

  return (
    <div>
      {/* Toolbar export / import */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: 'auto' }}>
          <span style={{ fontSize: '13px', color: '#888' }}>An fiscal:</span>
          <select value={an} onChange={e => setAn(parseInt(e.target.value))}
            style={{ padding: '6px 10px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '7px', outline: 'none', fontFamily: 'inherit' }}>
            {Array.from({ length: 5 }, (_, i) => anCurent - i).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button onClick={doExport}
          style={{ padding: '7px 14px', fontSize: '13px', border: '1.5px solid #2d2d7e', borderRadius: '8px', cursor: 'pointer', background: '#eef2ff', color: '#2d2d7e', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
          ⬇ Export CSV ({an})
        </button>
        <button onClick={() => fileInputRef.current?.click()} disabled={importing}
          style={{ padding: '7px 14px', fontSize: '13px', border: '1.5px solid #1d9e75', borderRadius: '8px', cursor: 'pointer', background: '#f0fdf4', color: '#166534', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {importing ? '⏳ Se importă...' : '⬆ Import CSV'}
        </button>
        <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={doImport} />
      </div>

      {/* Rezultat import */}
      {importRez && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontWeight: '700', fontSize: '14px' }}>Rezultat import</span>
            <button onClick={() => setImportRez(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '16px' }}>×</button>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: importRez.erori?.length ? '10px' : 0 }}>
            <span style={{ color: '#166534', fontSize: '13px' }}>✓ {importRez.importate} înregistrate noi</span>
            <span style={{ color: '#2d2d7e', fontSize: '13px' }}>↻ {importRez.actualizate} actualizate</span>
            {importRez.erori?.length > 0 && <span style={{ color: '#dc2626', fontSize: '13px' }}>✗ {importRez.erori.length} erori</span>}
          </div>
          {importRez.erori?.length > 0 && (
            <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#991b1b', maxHeight: '120px', overflowY: 'auto' }}>
              {importRez.erori.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}>
            <strong>Format CSV import:</strong> cnp_sau_cui, tip_proprietate (cladire/teren/transport), adresa, an, suma_totala, observatii
          </div>
        </div>
      )}

      {/* Căutare cetățean */}
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <form onSubmit={onSearch} style={{ display: 'flex', gap: '10px' }}>
          <input
            value={cautare} onChange={e => setCautare(e.target.value)}
            placeholder="Căutare după nume, CNP sau email..."
            style={{ flex: 1, padding: '9px 12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', fontFamily: 'inherit' }}
          />
          <button type="submit" disabled={loadSearch} style={{ padding: '9px 18px', background: '#2d2d7e', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            {loadSearch ? '...' : 'Caută'}
          </button>
        </form>
        {rezultate.length > 0 && (
          <div style={{ marginTop: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            {rezultate.map(u => (
              <div key={u.id} onClick={() => selectCetatean(u)}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                         display: 'flex', gap: '10px', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <span style={{ fontSize: '20px' }}>{u.tip_persoana === 'juridica' ? '🏢' : '👤'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>{u.prenume} {u.nume}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{u.cnp || (u.email?.endsWith('@portofelet.local') ? '🪪 EUDI Wallet' : u.email)} · {u.nr_proprietati} proprietăți</div>
                </div>
                <span style={{ color: '#9ca3af' }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cetățean selectat */}
      {cetatean && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                        background: '#eef2ff', borderRadius: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px' }}>{cetatean.tip_persoana === 'juridica' ? '🏢' : '👤'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a2e' }}>{cetatean.prenume} {cetatean.nume}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{cetatean.cnp} · {cetatean.email?.endsWith('@portofelet.local') ? '🪪 EUDI Wallet' : cetatean.email}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select value={an} onChange={e => setAn(parseInt(e.target.value))}
                style={{ padding: '6px 10px', fontSize: '13px', border: '1.5px solid #c7d2fe', borderRadius: '7px', outline: 'none', fontFamily: 'inherit' }}>
                {Array.from({ length: 5 }, (_, i) => anCurent - i).map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <button onClick={() => { setCetatean(null); setCautare(''); }}
                style={{ padding: '6px 12px', fontSize: '13px', border: '1px solid #c7d2fe', borderRadius: '7px', cursor: 'pointer', background: '#fff', color: '#4338ca' }}>
                × Schimbă
              </button>
              <button onClick={() => setModalProp('new')}
                style={{ padding: '6px 12px', fontSize: '13px', border: 'none', borderRadius: '7px', cursor: 'pointer', background: '#2d2d7e', color: '#fff', fontWeight: '600' }}>
                + Proprietate
              </button>
            </div>
          </div>

          {/* Lista proprietăți */}
          {(loadProps || loadImpoz) ? <Spinner /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(proprietati || []).length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '14px' }}>
                  Nicio proprietate înregistrată. Apăsați "+ Proprietate" pentru a adăuga.
                </div>
              )}
              {(proprietati || []).map(prop => {
                const impRaw = (impozite || []).find(i => i.id === prop.id);
                return (
                  <div key={prop.id} style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>
                      <span style={{ fontSize: '22px' }}>{TIP_PROP.find(t => t.value === prop.tip)?.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>
                          {prop.adresa || [prop.marca, prop.model, prop.nr_inmatriculare].filter(Boolean).join(' ') || prop.tip}
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                          {prop.tip === 'cladire' && [prop.tip_cladire, prop.suprafata_utila && `${prop.suprafata_utila} mp`, prop.an_constructie && `an ${prop.an_constructie}`].filter(Boolean).join(' · ')}
                          {prop.tip === 'teren' && [prop.suprafata_teren && `${prop.suprafata_teren} mp`, prop.categorie_teren, prop.zona_teren && `Zona ${prop.zona_teren}`].filter(Boolean).join(' · ')}
                          {prop.tip === 'transport' && [prop.capacitate_cilindrica && `${prop.capacitate_cilindrica} cm³`, prop.an_fabricatie && `an ${prop.an_fabricatie}`].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => setModalProp(prop)}
                          style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: '#f9fafb' }}>
                          Editează
                        </button>
                        <button onClick={() => setModalImpoz(prop)}
                          style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #2d2d7e', borderRadius: '6px', cursor: 'pointer', background: '#2d2d7e', color: '#fff' }}>
                          {impRaw?.impozit_id ? 'Impozit' : '+ Impozit'}
                        </button>
                      </div>
                    </div>
                    {/* Impozit curent */}
                    {impRaw?.impozit_id && (
                      <div style={{ borderTop: '1px solid #f3f4f6', padding: '10px 16px', background: '#fafafa',
                                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', alignItems: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#374151' }}>
                          <span style={{ fontWeight: '700' }}>{fmtL(impRaw.suma_totala)}</span>
                          <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>{an}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: impRaw.platit_rata1 || impRaw.platit_integral ? '#22c55e' : '#ef4444', flexShrink: 0 }} />
                            Rata I: {fmtL(impRaw.rata1)}
                            {!impRaw.platit_rata1 && !impRaw.platit_integral && (
                              <button onClick={() => setModalPlata({ impozit: impRaw, rata: 1 })}
                                style={{ marginLeft: 'auto', padding: '2px 7px', fontSize: '11px', border: '1px solid #22c55e', borderRadius: '5px', cursor: 'pointer', background: '#f0fdf4', color: '#166534' }}>
                                Înreg. plată
                              </button>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: impRaw.platit_rata2 || impRaw.platit_integral ? '#22c55e' : '#ef4444', flexShrink: 0 }} />
                            Rata II: {fmtL(impRaw.rata2)}
                            {!impRaw.platit_rata2 && !impRaw.platit_integral && (
                              <button onClick={() => setModalPlata({ impozit: impRaw, rata: 2 })}
                                style={{ marginLeft: 'auto', padding: '2px 7px', fontSize: '11px', border: '1px solid #22c55e', borderRadius: '5px', cursor: 'pointer', background: '#f0fdf4', color: '#166534' }}>
                                Înreg. plată
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {impRaw.platit_integral
                            ? <span style={{ fontSize: '12px', color: '#166534', fontWeight: '600' }}>✓ Achitat integral</span>
                            : (!impRaw.platit_rata1 || !impRaw.platit_rata2) && (
                              <button onClick={() => setModalPlata({ impozit: impRaw, rata: 'integral' })}
                                style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #2d2d7e', borderRadius: '6px', cursor: 'pointer', background: '#eef2ff', color: '#2d2d7e', fontWeight: '600' }}>
                                Plată integrală
                              </button>
                            )
                          }
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal: Adaugă/Editează Proprietate */}
      {modalProp !== null && (
        <ModalProprietate
          prop={modalProp === 'new' ? null : modalProp}
          proprietarId={cetatean?.id}
          onClose={() => setModalProp(null)}
          onSaved={() => { setModalProp(null); refetch(); }}
        />
      )}

      {/* Modal: Adaugă/Editează Impozit */}
      {modalImpoz && (
        <ModalImpozit
          prop={modalImpoz}
          an={an}
          onClose={() => setModalImpoz(null)}
          onSaved={() => { setModalImpoz(null); refetch(); }}
        />
      )}

      {/* Modal: Înregistrare Plată */}
      {modalPlata && (
        <ModalPlata
          impozit={modalPlata.impozit}
          rata={modalPlata.rata}
          onClose={() => setModalPlata(null)}
          onSaved={() => { setModalPlata(null); refetch(); }}
        />
      )}
    </div>
  );
}

function ModalProprietate({ prop, proprietarId, onClose, onSaved }) {
  const isEdit = !!prop;
  const [tip, setTip]     = useState(prop?.tip || 'cladire');
  const [form, setForm]   = useState({
    adresa: prop?.adresa || '',
    observatii: prop?.observatii || '',
    // cladire
    suprafata_construita: prop?.suprafata_construita || '', suprafata_utila: prop?.suprafata_utila || '',
    tip_cladire: prop?.tip_cladire || 'rezidentiala', an_constructie: prop?.an_constructie || '', nr_camere: prop?.nr_camere || '',
    // teren
    suprafata_teren: prop?.suprafata_teren || '', categorie_teren: prop?.categorie_teren || CATEGORII_TEREN[0], zona_teren: prop?.zona_teren || 'A',
    // transport
    marca: prop?.marca || '', model: prop?.model || '', nr_inmatriculare: prop?.nr_inmatriculare || '',
    an_fabricatie: prop?.an_fabricatie || '', capacitate_cilindrica: prop?.capacitate_cilindrica || '', tip_transport: prop?.tip_transport || TIP_TRANSPORT_OPT[0],
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = { tip, ...form, proprietar_id: proprietarId };
      if (isEdit) await adminAPI.updateProprietate(prop.id, payload);
      else        await adminAPI.createProprietate(payload);
      toast.success(isEdit ? 'Proprietate actualizată' : 'Proprietate adăugată');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Eroare'); }
    finally { setSaving(false); }
  }

  const I = swf.inp;
  const L = swf.lbl;
  const F = swf.fld;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: '17px', fontWeight: '700' }}>
          {isEdit ? 'Editează proprietate' : 'Adaugă proprietate'}
        </h3>

        {!isEdit && (
          <div style={{ ...F, marginBottom: '16px' }}>
            <label style={L}>Tip proprietate</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {TIP_PROP.map(t => (
                <button key={t.value} onClick={() => setTip(t.value)}
                  style={{ flex: 1, padding: '8px', border: `2px solid ${tip === t.value ? '#2d2d7e' : '#e5e7eb'}`,
                           borderRadius: '8px', background: tip === t.value ? '#eef2ff' : '#fff',
                           cursor: 'pointer', fontSize: '13px', fontWeight: tip === t.value ? '700' : '400' }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={F}><label style={L}>Adresă / Localizare</label><input style={I} value={form.adresa} onChange={e => set('adresa', e.target.value)} /></div>

          {tip === 'cladire' && (<>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={F}><label style={L}>Suprafață construită (mp)</label><input style={I} type="number" value={form.suprafata_construita} onChange={e => set('suprafata_construita', e.target.value)} /></div>
              <div style={F}><label style={L}>Suprafață utilă (mp)</label><input style={I} type="number" value={form.suprafata_utila} onChange={e => set('suprafata_utila', e.target.value)} /></div>
              <div style={F}><label style={L}>Tip clădire</label>
                <select style={I} value={form.tip_cladire} onChange={e => set('tip_cladire', e.target.value)}>
                  <option value="rezidentiala">Rezidențială</option>
                  <option value="nerezidentiala">Nerezidențială</option>
                </select>
              </div>
              <div style={F}><label style={L}>An construcție</label><input style={I} type="number" value={form.an_constructie} onChange={e => set('an_constructie', e.target.value)} /></div>
              <div style={F}><label style={L}>Nr. camere</label><input style={I} type="number" value={form.nr_camere} onChange={e => set('nr_camere', e.target.value)} /></div>
            </div>
          </>)}

          {tip === 'teren' && (<>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={F}><label style={L}>Suprafață (mp)</label><input style={I} type="number" value={form.suprafata_teren} onChange={e => set('suprafata_teren', e.target.value)} /></div>
              <div style={F}><label style={L}>Zonă (A/B/C/D)</label>
                <select style={I} value={form.zona_teren} onChange={e => set('zona_teren', e.target.value)}>
                  {['A','B','C','D'].map(z => <option key={z}>{z}</option>)}
                </select>
              </div>
              <div style={{ ...F, gridColumn: '1/-1' }}><label style={L}>Categorie teren</label>
                <select style={I} value={form.categorie_teren} onChange={e => set('categorie_teren', e.target.value)}>
                  {CATEGORII_TEREN.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
          </>)}

          {tip === 'transport' && (<>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={F}><label style={L}>Marcă</label><input style={I} value={form.marca} onChange={e => set('marca', e.target.value)} /></div>
              <div style={F}><label style={L}>Model</label><input style={I} value={form.model} onChange={e => set('model', e.target.value)} /></div>
              <div style={F}><label style={L}>Nr. înmatriculare</label><input style={I} value={form.nr_inmatriculare} onChange={e => set('nr_inmatriculare', e.target.value)} /></div>
              <div style={F}><label style={L}>An fabricație</label><input style={I} type="number" value={form.an_fabricatie} onChange={e => set('an_fabricatie', e.target.value)} /></div>
              <div style={F}><label style={L}>Capacitate cilindrică (cm³)</label><input style={I} type="number" value={form.capacitate_cilindrica} onChange={e => set('capacitate_cilindrica', e.target.value)} /></div>
              <div style={F}><label style={L}>Tip</label>
                <select style={I} value={form.tip_transport} onChange={e => set('tip_transport', e.target.value)}>
                  {TIP_TRANSPORT_OPT.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </>)}

          <div style={F}><label style={L}>Observații</label><textarea style={{ ...I, resize: 'vertical', minHeight: '60px' }} value={form.observatii} onChange={e => set('observatii', e.target.value)} /></div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: '#fff' }}>Anulează</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 18px', fontSize: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: '#2d2d7e', color: '#fff', fontWeight: '600' }}>
            {saving ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalImpozit({ prop, an, onClose, onSaved }) {
  const [suma, setSuma] = useState('');
  const [obs, setObs]   = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!suma || isNaN(parseFloat(suma))) return toast.error('Introduceți suma totală');
    setSaving(true);
    try {
      await adminAPI.calculeazaImpozit({ proprietate_id: prop.id, an, suma_totala: parseFloat(suma), observatii: obs });
      toast.success('Impozit salvat');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Eroare'); }
    finally { setSaving(false); }
  }

  const rata1 = suma ? (Math.round(parseFloat(suma) / 2 * 100) / 100).toFixed(2) : '—';
  const rata2 = suma ? (parseFloat(suma) - parseFloat(rata1)).toFixed(2) : '—';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '420px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: '700' }}>Impozit {an}</h3>
        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#888' }}>
          {prop.adresa || [prop.marca, prop.model, prop.nr_inmatriculare].filter(Boolean).join(' ') || prop.tip}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={swf.fld}>
            <label style={swf.lbl}>Sumă totală anuală (lei)</label>
            <input style={swf.inp} type="number" step="0.01" min="0" value={suma} onChange={e => setSuma(e.target.value)} placeholder="ex: 450.00" autoFocus />
          </div>
          {suma && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={{ padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Rata I (31 mart.)</div>
                <div style={{ fontWeight: '700', color: '#2d2d7e' }}>{rata1} lei</div>
              </div>
              <div style={{ padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Rata II (30 sept.)</div>
                <div style={{ fontWeight: '700', color: '#2d2d7e' }}>{rata2} lei</div>
              </div>
            </div>
          )}
          <div style={swf.fld}>
            <label style={swf.lbl}>Observații (opțional)</label>
            <input style={swf.inp} value={obs} onChange={e => setObs(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: '#fff' }}>Anulează</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 18px', fontSize: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: '#2d2d7e', color: '#fff', fontWeight: '600' }}>
            {saving ? 'Se salvează...' : 'Salvează'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalPlata({ impozit, rata, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ data_plata: today, nr_chitanta: '', metoda_plata: 'numerar' });
  const [saving, setSaving] = useState(false);

  const label = rata === 'integral' ? 'Plată integrală' : `Rata ${rata === 1 ? 'I' : 'II'}`;
  const suma  = rata === 'integral'
    ? impozit.suma_totala
    : rata === 1 ? impozit.rata1 : impozit.rata2;

  async function save() {
    if (!form.data_plata) return toast.error('Completați data plății');
    setSaving(true);
    try {
      await adminAPI.marcheazaPlatit(impozit.impozit_id, { rata, ...form });
      toast.success('Plată înregistrată');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Eroare'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '380px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '700' }}>Înregistrare plată</h3>
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#888' }}>{label} — {fmtL(suma)}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={swf.fld}>
            <label style={swf.lbl}>Data plății *</label>
            <input style={swf.inp} type="date" value={form.data_plata} onChange={e => setForm(f => ({ ...f, data_plata: e.target.value }))} />
          </div>
          <div style={swf.fld}>
            <label style={swf.lbl}>Nr. chitanță / document</label>
            <input style={swf.inp} value={form.nr_chitanta} onChange={e => setForm(f => ({ ...f, nr_chitanta: e.target.value }))} placeholder="ex: 12345" />
          </div>
          <div style={swf.fld}>
            <label style={swf.lbl}>Metodă plată</label>
            <select style={swf.inp} value={form.metoda_plata} onChange={e => setForm(f => ({ ...f, metoda_plata: e.target.value }))}>
              {METODE_PLATA.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', background: '#fff' }}>Anulează</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 18px', fontSize: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: '#22c55e', color: '#fff', fontWeight: '600' }}>
            {saving ? '...' : 'Confirmă plata'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TabDocReqs ────────────────────────────────────────────────
const TIP_CERERE_LABELS = {
  sesizare: 'Sesizare', solicitare: 'Solicitare', petitie: 'Petiție',
  reclamatie: 'Reclamație', informatie: 'Cerere de informații',
};

function TabDocReqs() {
  const qc = useQueryClient();
  const { data: reqs, isLoading } = useQuery('admin-doc-reqs', adminAPI.getDocReqs);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ denumire: '', descriere: '', tip_cerere: '', categorie: '', obligatoriu: true, ordine: 0 });
  const [saving, setSaving] = useState(false);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save(e) {
    e.preventDefault();
    if (!form.denumire.trim()) return toast.error('Denumirea este obligatorie');
    setSaving(true);
    try {
      await adminAPI.createDocReq(form);
      toast.success('Document adăugat!');
      qc.invalidateQueries('admin-doc-reqs');
      setShowForm(false);
      setForm({ denumire: '', descriere: '', tip_cerere: '', categorie: '', obligatoriu: true, ordine: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eroare la salvare');
    } finally { setSaving(false); }
  }

  async function del(req) {
    if (!window.confirm(`Ștergeți cerința "${req.denumire}"?`)) return;
    try {
      await adminAPI.deleteDocReq(req.id);
      toast.success('Șters');
      qc.invalidateQueries('admin-doc-reqs');
    } catch { toast.error('Eroare la ștergere'); }
  }

  async function toggle(req) {
    try {
      await adminAPI.patchDocReq(req.id, { activ: !req.activ });
      qc.invalidateQueries('admin-doc-reqs');
    } catch { toast.error('Eroare'); }
  }

  const grouped = (reqs || []).reduce((acc, r) => {
    const key = r.tip_cerere || '—';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '700', color: '#1a1a2e' }}>Documente obligatorii per cerere</h2>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
            Configurați ce documente trebuie să atașeze cetățenii la depunerea fiecărui tip de cerere.
          </p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? '✕ Anulează' : '+ Adaugă document'}</Button>
      </div>

      {showForm && (
        <Card title="Document nou" style={{ marginBottom: '20px' }}>
          <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={swf.fld}>
                <label style={swf.lbl}>Denumire document *</label>
                <input style={swf.inp} value={form.denumire} onChange={e => setF('denumire', e.target.value)}
                  placeholder="ex: Buletin de identitate, Cerere tip..." />
              </div>
              <div style={swf.fld}>
                <label style={swf.lbl}>Tip cerere (gol = toate)</label>
                <select style={swf.inp} value={form.tip_cerere} onChange={e => setF('tip_cerere', e.target.value)}>
                  <option value="">Toate tipurile</option>
                  {Object.entries(TIP_CERERE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.5fr', gap: '12px' }}>
              <div style={swf.fld}>
                <label style={swf.lbl}>Descriere (opțional)</label>
                <input style={swf.inp} value={form.descriere} onChange={e => setF('descriere', e.target.value)}
                  placeholder="Instrucțiuni pentru cetățean..." />
              </div>
              <div style={swf.fld}>
                <label style={swf.lbl}>Categorie cerere (gol = toate)</label>
                <input style={swf.inp} value={form.categorie} onChange={e => setF('categorie', e.target.value)}
                  placeholder="ex: Urbanism, Stare Civilă..." />
              </div>
              <div style={swf.fld}>
                <label style={swf.lbl}>Ordine</label>
                <input style={swf.inp} type="number" min="0" value={form.ordine}
                  onChange={e => setF('ordine', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.obligatoriu} onChange={e => setF('obligatoriu', e.target.checked)} />
                Document obligatoriu
              </label>
              <Button type="submit" disabled={saving}>{saving ? 'Se salvează...' : '💾 Salvează'}</Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading && <Spinner message="Se încarcă..." />}

      {!isLoading && Object.keys(grouped).length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#888' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>📎</div>
          <p>Nicio cerință configurată. Apăsați „+ Adaugă document" pentru a începe.</p>
        </div>
      )}

      {Object.keys(grouped).sort().map(tip => (
        <div key={tip} style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px',
                       paddingBottom: '6px', borderBottom: '2px solid #e5e7eb' }}>
            {tip === '—' ? 'Toate tipurile de cerere' : TIP_CERERE_LABELS[tip] || tip}
            <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400, marginLeft: '8px' }}>
              {grouped[tip].length} {grouped[tip].length === 1 ? 'document' : 'documente'}
            </span>
          </h3>
          <Card>
            <div style={{ ...s.thRow, gridTemplateColumns: '2fr 1fr 0.6fr 0.5fr 1.2fr' }}>
              {['Denumire', 'Categorie', 'Obligatoriu', 'Ordine', 'Acțiuni'].map(h => (
                <span key={h} style={s.th}>{h}</span>
              ))}
            </div>
            {grouped[tip].map(r => (
              <div key={r.id} style={{ ...s.tdRow, gridTemplateColumns: '2fr 1fr 0.6fr 0.5fr 1.2fr', opacity: r.activ ? 1 : 0.5 }}>
                <div style={s.td}>
                  <div style={{ fontWeight: '600', fontSize: '13px' }}>{r.denumire}</div>
                  {r.descriere && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{r.descriere}</div>}
                </div>
                <span style={s.td}>{r.categorie || '—'}</span>
                <span style={{ ...s.td, color: r.obligatoriu ? '#15803d' : '#9ca3af', fontWeight: '600' }}>
                  {r.obligatoriu ? 'Da' : 'Nu'}
                </span>
                <span style={s.td}>{r.ordine}</span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => toggle(r)} style={{ ...s.btnMic, color: r.activ ? '#d97706' : '#15803d' }}>
                    {r.activ ? 'Dezact.' : 'Activ.'}
                  </button>
                  <button onClick={() => del(r)} style={{ ...s.btnMic, color: '#dc2626' }}>Șterge</button>
                </div>
              </div>
            ))}
          </Card>
        </div>
      ))}
    </div>
  );
}

// ── TabTipizate ───────────────────────────────────────────────
const TIP_CERERE_OPT = [
  { value: '', label: 'Orice tip de cerere' },
  { value: 'sesizare',   label: 'Sesizare' },
  { value: 'solicitare', label: 'Solicitare' },
  { value: 'petitie',    label: 'Petiție' },
  { value: 'reclamatie', label: 'Reclamație' },
  { value: 'informatie', label: 'Cerere de informații' },
];

function TabTipizate() {
  const qc = useQueryClient();
  const { data: tipizate, isLoading } = useQuery('admin-tipizate', adminAPI.getTipizate);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titlu: '', descriere: '', categorie: '', tip_cerere: '' });
  const [fisier, setFisier] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef();

  const CATEGORII_DEFAULT = [
    'Stare Civilă', 'Urbanism și Amenajarea Teritoriului', 'Taxe și Impozite',
    'Asistență Socială', 'Registratură', 'Cadastru și Proprietăți',
    'Autorizații și Avize', 'Petiții și Sesizări', 'Diverse',
  ];

  const { data: categoriiDB } = useQuery('tipizate-categorii',
    () => api.get('/tipizate/categorii').then(r => r.data.data),
    { staleTime: 60000 }
  );

  const categoriiExistente = React.useMemo(() => {
    const db = categoriiDB || [];
    const all = [...new Set([...CATEGORII_DEFAULT, ...db])].sort();
    return all;
  }, [categoriiDB]);

  const [categorieNouaMode, setCategorieNouaMode] = React.useState(false);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function upload(e) {
    e.preventDefault();
    if (!fisier) return toast.error('Selectați un fișier');
    if (!form.titlu.trim()) return toast.error('Titlul este obligatoriu');
    if (!form.categorie.trim()) return toast.error('Categoria este obligatorie');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('fisier', fisier);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      await adminAPI.uploadTipizat(fd);
      toast.success('Formular adăugat cu succes!');
      qc.invalidateQueries('admin-tipizate');
      setShowForm(false);
      setForm({ titlu: '', descriere: '', categorie: '', tip_cerere: '' });
      setFisier(null);
      setCategorieNouaMode(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eroare la upload');
    } finally { setSaving(false); }
  }

  async function toggle(id) {
    try {
      await adminAPI.toggleTipizat(id);
      qc.invalidateQueries('admin-tipizate');
    } catch { toast.error('Eroare'); }
  }

  async function del(t) {
    if (!window.confirm(`Ștergeți formularul "${t.titlu}"?`)) return;
    try {
      await adminAPI.deleteTipizat(t.id);
      toast.success('Șters');
      qc.invalidateQueries('admin-tipizate');
    } catch { toast.error('Eroare la ștergere'); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#1a1a2e' }}>Formulare tipizate</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Anulează' : '+ Adaugă formular'}
        </Button>
      </div>

      {showForm && (
        <Card title="Upload formular nou" style={{ marginBottom: '20px' }}>
          <form onSubmit={upload} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={swf.fld}>
                <label style={swf.lbl}>Titlu *</label>
                <input style={swf.inp} value={form.titlu} onChange={e => setF('titlu', e.target.value)} placeholder="ex: Cerere eliberare certificat" />
              </div>
              <div style={swf.fld}>
                <label style={swf.lbl}>Categorie *</label>
                {categorieNouaMode ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input style={{ ...swf.inp, flex: 1 }} autoFocus
                      value={form.categorie} onChange={e => setF('categorie', e.target.value)}
                      placeholder="Numele categoriei noi" />
                    <button type="button" onClick={() => { setCategorieNouaMode(false); setF('categorie', ''); }}
                      style={{ padding: '0 10px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#f3f4f6', cursor: 'pointer', fontSize: '13px' }}>
                      ✕
                    </button>
                  </div>
                ) : (
                  <select style={swf.inp}
                    value={form.categorie}
                    onChange={e => {
                      if (e.target.value === '__nou__') { setCategorieNouaMode(true); setF('categorie', ''); }
                      else setF('categorie', e.target.value);
                    }}>
                    <option value="">— Alege categorie —</option>
                    {categoriiExistente.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__nou__">+ Categorie nouă...</option>
                  </select>
                )}
              </div>
            </div>
            <div style={swf.fld}>
              <label style={swf.lbl}>Descriere (opțional)</label>
              <input style={swf.inp} value={form.descriere} onChange={e => setF('descriere', e.target.value)} placeholder="Scurtă descriere a formularului" />
            </div>
            <div style={swf.fld}>
              <label style={swf.lbl}>Tip cerere asociat</label>
              <select style={swf.inp} value={form.tip_cerere} onChange={e => setF('tip_cerere', e.target.value)}>
                {TIP_CERERE_OPT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={swf.fld}>
              <label style={swf.lbl}>Fișier * (PDF, DOC, DOCX, ODT — max 20 MB)</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button type="button" onClick={() => fileRef.current?.click()}
                  style={{ padding: '8px 16px', border: '1.5px dashed #d1d5db', borderRadius: '8px', background: '#f9fafb', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                  {fisier ? `📄 ${fisier.name}` : '📁 Alege fișier'}
                </button>
                {fisier && <span style={{ fontSize: '12px', color: '#6b7280' }}>{(fisier.size / 1024).toFixed(0)} KB</span>}
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.odt" style={{ display: 'none' }}
                  onChange={e => setFisier(e.target.files[0] || null)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" disabled={saving}>{saving ? 'Se încarcă...' : '⬆ Upload'}</Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading && <Spinner message="Se încarcă formularele..." />}

      {!isLoading && (!tipizate || tipizate.length === 0) && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📂</div>
          <p>Nu există formulare tipizate. Adăugați primul formular.</p>
        </div>
      )}

      {(tipizate || []).length > 0 && (
        <Card>
          <div style={{ ...s.thRow, gridTemplateColumns: '2fr 1.2fr 1fr 0.8fr 0.7fr 1.2fr' }}>
            {['Titlu', 'Categorie', 'Tip cerere', 'Format', 'Descărcări', 'Acțiuni'].map(h => (
              <span key={h} style={s.th}>{h}</span>
            ))}
          </div>
          {(tipizate || []).map(t => (
            <div key={t.id} style={{ ...s.tdRow, gridTemplateColumns: '2fr 1.2fr 1fr 0.8fr 0.7fr 1.2fr', opacity: t.activ ? 1 : 0.5 }}>
              <div style={s.td}>
                <div style={{ fontWeight: '600', fontSize: '13px' }}>{t.titlu}</div>
                {t.descriere && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{t.descriere.slice(0, 60)}{t.descriere.length > 60 ? '…' : ''}</div>}
              </div>
              <span style={s.td}>{t.categorie}</span>
              <span style={s.td}>{t.tip_cerere || '—'}</span>
              <span style={{ ...s.td, textTransform: 'uppercase', fontWeight: '700', fontSize: '11px', color: t.fisier_tip === 'pdf' ? '#e24b4a' : '#2b579a' }}>{t.fisier_tip}</span>
              <span style={s.td}>{t.descarcat_ori}</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => toggle(t.id)} style={{ ...s.btnMic, color: t.activ ? '#d97706' : '#15803d' }}>
                  {t.activ ? 'Dezact.' : 'Activ.'}
                </button>
                <button onClick={() => del(t)} style={{ ...s.btnMic, color: '#dc2626' }}>Șterge</button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ── Tab Backup ──────────────────────────────────────────────────
function TabBackup() {
  const queryClient = useQueryClient();
  const { data: cfg, isLoading } = useQuery('backup-config', adminAPI.getBackupConfig);
  const { data: lista, refetch: refetchLista } = useQuery('backup-list', adminAPI.getBackupList);
  const [form, setForm] = useState({});
  const [testand, setTestand] = useState(false);
  const [testRezultat, setTestRezultat] = useState(null);
  const [ruland, setRuland] = useState(false);

  const saveMut = useMutation(adminAPI.saveBackupConfig, {
    onSuccess: () => { toast.success('Configurare backup salvată'); queryClient.invalidateQueries('backup-config'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Eroare salvare'),
  });

  if (isLoading) return <Spinner />;
  const valori = { ...cfg, ...form };
  const tip = valori.BACKUP_DESTINATIE_TIP || 'local';

  function set(cheie, valoare) {
    setForm(f => ({ ...f, [cheie]: valoare }));
  }

  async function handleTest() {
    setTestand(true); setTestRezultat(null);
    try {
      const r = await adminAPI.testBackupConn(form);
      setTestRezultat({ ok: true, mesaj: r.mesaj });
    } catch (err) {
      setTestRezultat({ ok: false, mesaj: err.response?.data?.message || 'Eroare conexiune' });
    } finally { setTestand(false); }
  }

  async function handleRun() {
    setRuland(true);
    try {
      const r = await adminAPI.runBackup();
      toast.success('Backup finalizat: ' + (r.data?.snapshot || '') + ' / ' + (r.data?.dump || ''));
      refetchLista();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Backup eșuat');
    } finally { setRuland(false); }
  }

  async function handleDownload(item) {
    try {
      const r = await adminAPI.downloadBackup(item.tip, item.fisier);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = item.fisier;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch {
      toast.error('Eroare la descărcare');
    }
  }

  return (
    <div>
      <Card title="Backup — pe 3 niveluri">
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '14px' }}>
          Nivel 1: snapshot JSON zilnic al tabelelor operaționale (păstrat 14 zile).{' '}
          Nivel 2: dump complet al bazei de date, comprimat și criptat AES-256.{' '}
          Nivel 3: sincronizare a dump-ului și a fișierelor din stocare către o destinație externă.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
          <Button onClick={handleRun} disabled={ruland}>
            {ruland ? 'Se rulează...' : '▶ Rulează backup acum'}
          </Button>
          {cfg?.BACKUP_LAST_RUN_AT && (
            <span style={{ fontSize: '12px', color: '#888' }}>
              Ultima rulare: {formatDataOra(cfg.BACKUP_LAST_RUN_AT)}
              {' '}({cfg.BACKUP_LAST_RUN_STATUS === 'ok' ? '✓ reușit' : '✗ eroare'})
            </span>
          )}
        </div>
      </Card>

      <Card title="Destinație externă (Nivel 3)">
        <div style={s.fld}>
          <label style={s.lbl}>Tip destinație</label>
          <select style={s.inp} value={tip} onChange={e => set('BACKUP_DESTINATIE_TIP', e.target.value)}>
            <option value="local">Folder local / NAS montat</option>
            <option value="s3">S3 (compatibil)</option>
            <option value="sftp">SFTP</option>
          </select>
        </div>

        {tip === 'local' && (
          <div style={{ ...s.fld, marginTop: '12px' }}>
            <label style={s.lbl}>Cale folder local/NAS</label>
            <input style={s.inp} placeholder="ex: D:\civicapp-backups sau /mnt/nas/civicapp"
              value={valori.BACKUP_LOCAL_PATH || ''} onChange={e => set('BACKUP_LOCAL_PATH', e.target.value)} />
          </div>
        )}

        {tip === 's3' && (
          <div style={{ ...s.formGrid, marginTop: '12px' }}>
            <div style={s.fld}><label style={s.lbl}>Endpoint</label>
              <input style={s.inp} placeholder="s3.eu-central-1.amazonaws.com" value={valori.BACKUP_S3_ENDPOINT || ''} onChange={e => set('BACKUP_S3_ENDPOINT', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Port</label>
              <input style={s.inp} value={valori.BACKUP_S3_PORT || '443'} onChange={e => set('BACKUP_S3_PORT', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Bucket</label>
              <input style={s.inp} value={valori.BACKUP_S3_BUCKET || ''} onChange={e => set('BACKUP_S3_BUCKET', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Foloseste SSL</label>
              <select style={s.inp} value={valori.BACKUP_S3_USE_SSL || 'true'} onChange={e => set('BACKUP_S3_USE_SSL', e.target.value)}>
                <option value="true">Da</option><option value="false">Nu</option>
              </select></div>
            <div style={s.fld}><label style={s.lbl}>Access Key</label>
              <input style={s.inp} value={valori.BACKUP_S3_ACCESS_KEY || ''} onChange={e => set('BACKUP_S3_ACCESS_KEY', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Secret Key</label>
              <input style={s.inp} type="password" placeholder="••••••••" value={valori.BACKUP_S3_SECRET_KEY || ''} onChange={e => set('BACKUP_S3_SECRET_KEY', e.target.value)} /></div>
          </div>
        )}

        {tip === 'sftp' && (
          <div style={{ ...s.formGrid, marginTop: '12px' }}>
            <div style={s.fld}><label style={s.lbl}>Host</label>
              <input style={s.inp} value={valori.BACKUP_SFTP_HOST || ''} onChange={e => set('BACKUP_SFTP_HOST', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Port</label>
              <input style={s.inp} value={valori.BACKUP_SFTP_PORT || '22'} onChange={e => set('BACKUP_SFTP_PORT', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Utilizator</label>
              <input style={s.inp} value={valori.BACKUP_SFTP_USER || ''} onChange={e => set('BACKUP_SFTP_USER', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Parolă</label>
              <input style={s.inp} type="password" placeholder="••••••••" value={valori.BACKUP_SFTP_PASSWORD || ''} onChange={e => set('BACKUP_SFTP_PASSWORD', e.target.value)} /></div>
            <div style={s.fld}><label style={s.lbl}>Cale remotă</label>
              <input style={s.inp} value={valori.BACKUP_SFTP_REMOTE_PATH || '/backups/civicapp'} onChange={e => set('BACKUP_SFTP_REMOTE_PATH', e.target.value)} /></div>
          </div>
        )}

        <div style={{ ...s.fld, marginTop: '12px', maxWidth: '220px' }}>
          <label style={s.lbl}>Retenție dump complet (zile)</label>
          <input style={s.inp} type="number" min="1" value={valori.BACKUP_DUMP_RETENTION_DAYS || '30'}
            onChange={e => set('BACKUP_DUMP_RETENTION_DAYS', e.target.value)} />
        </div>

        {testRezultat && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: testRezultat.ok ? '#16803c' : '#dc2626' }}>
            {testRezultat.ok ? '✓ ' : '✗ '}{testRezultat.mesaj}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <Button variant="secondary" onClick={handleTest} disabled={testand}>
            {testand ? 'Se testează...' : 'Testează conexiunea'}
          </Button>
          <Button onClick={() => saveMut.mutate(form)} disabled={saveMut.isLoading || Object.keys(form).length === 0}>
            {saveMut.isLoading ? 'Se salvează...' : 'Salvează configurarea'}
          </Button>
        </div>
      </Card>

      <Card title={`Backup-uri locale (${lista?.length || 0})`}>
        {(!lista || lista.length === 0) && <p style={{ fontSize: '13px', color: '#888' }}>Niciun backup local încă — apasă „Rulează backup acum”.</p>}
        {lista && lista.length > 0 && (
          <div>
            {lista.map(item => (
              <div key={item.tip + item.fisier} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f5f5f5', fontSize: '13px' }}>
                <span style={{ fontSize: '11px', background: item.tip === 'dump' ? '#2d2d7e' : '#888', color: '#fff', borderRadius: '4px', padding: '1px 6px', flexShrink: 0 }}>{item.tip}</span>
                <span style={{ flex: 1, fontFamily: 'monospace', fontSize: '12px' }}>{item.fisier}</span>
                <span style={{ color: '#888', fontSize: '12px' }}>{(item.marime / 1024).toFixed(0)} KB</span>
                <span style={{ color: '#888', fontSize: '12px' }}>{formatDataOra(item.creat_la)}</span>
                <button style={s.btnMic} onClick={() => handleDownload(item)}>↓ Descarcă</button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// Stiluri locale TabFluxuri / WorkflowEditor
const swf = {
  btn:     { padding: '6px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '7px', cursor: 'pointer', background: '#f9fafb', color: '#374151', whiteSpace: 'nowrap' },
  btnIcon: { width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: '#f9fafb', color: '#555', fontSize: '13px', padding: 0 },
  fld:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  lbl:     { fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' },
  inp:     { padding: '9px 12px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', width: '100%' },
  inpS:    { padding: '6px 10px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '7px', outline: 'none', fontFamily: 'inherit', width: '100%' },
};

const s = {
  tabs:    { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #f0f0f0', overflowX: 'auto' },
  tabBtn:  { padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#888', borderBottom: '2px solid transparent', marginBottom: '-2px', whiteSpace: 'nowrap' },
  tabActive: { color: '#2d2d7e', borderBottomColor: '#2d2d7e' },
  grid3:   { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', marginBottom: '20px' },
  infoRow: { display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f5f5f5', fontSize: '14px' },
  infoKey: { color: '#888', minWidth: '100px', flexShrink: 0 },
  infoVal: { color: '#1a1a2e', fontWeight: '500' },
  formGrid:{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fld:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  lbl:     { fontSize: '12px', fontWeight: '500', color: '#666' },
  inp:     { padding: '8px 12px', fontSize: '14px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', fontFamily: 'inherit' },
  errMsg:  { fontSize: '11px', color: '#dc2626' },
  thRow:   { display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr 0.7fr 1.5fr 1.5fr', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '4px', gap: '8px' },
  tdRow:   { display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr 0.7fr 1.5fr 1.5fr', padding: '10px 12px', borderBottom: '1px solid #f5f5f5', alignItems: 'center', gap: '8px' },
  th:      { fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase' },
  td:      { fontSize: '13px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  btnMic:  { padding: '4px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#fff', color: '#555', whiteSpace: 'nowrap' },
};